// assets/wc-selectors.js
// Format/size/frame/color selectors. PROTECTED behavior — lifted byte-for-byte
// from wolden-customize.js monolith (Sub-project B Task B.3.3).
// Spec: "preserved byte-for-byte". DO NOT refactor, rename, or dedupe.
//
// Monolith line ranges lifted:
//   1418–1671   initPdpOptionsListener
//   1673–1839   initPdpOptionsState (includes child-PDP hydration)
//   1841–1968   updatePdpVariantState, _buyButtonsFormScope, _writeFormProperty
//   3248–3360   Pricing/color constants + getCurrentPricing/fmtPrice/fmtSavings/getCurrentVariantId
//   3432–3584   _selectionScopes, _setSelected, refreshActiveBranches, refreshSizeFramePills
//   3586–3781   lookupFramedMockup, renderCanvasFramedPdpGallery, preloadFramedMockups, applyFramedMockupToGallery
//   3783–3848   buildSlotModelForFormat
//   3850–4006   applySlotModelToGallery, getPrimaryGalleryScope
//   4008–4147   renderUnifiedGallery, captureFramedSlotIntent, syncRadioGroupBy, transitionGalleryForFormatChange
//   4149–4331   resetFormatDefaults, restorePdpGalleryVisibility, installFramedGallerySrcGuard
//   5102        WC._slider initialization
//   5134–5174   setSlideIndex, nextSlide, prevSlide, CANVAS_SIZE_TO_SLIDE
//   7744–7935   modal selector handlers (select-format/size/frame/color inside modal click listener)
//
// Cross-cutting calls to functions still in the monolith are preserved verbatim
// via `typeof fn === 'function'` guards (refreshMockupImages, refreshPricingDisplay,
// applyCleanBreakNow, applyCleanBreakDeferred, bootstrapStateFromCurrentChildPdp,
// applyPdpVisibility, refreshAtcReadyState, wiggleUploadZone, getModal, track, log,
// switchToGen, closeModal, addCurrentSelectionToCart, regenerate, openQualityCheck,
// zoomCurrentSlide, sharePreview, downloadPreview, openLightbox, setModalState).
// WC._currentResult and other WC.* state slots are shared globals.

import { log, track, wcWriteCartAttributes } from './wc77-analytics.js';
import { getActiveVariant } from './wc77-experiments.js';
import { getModal, setModalState } from './wc2-modal.js';
// 2026-05-17 — Live-binding import for refreshMockupImages. wc5-result.js
// imports from wc5-selectors.js → if not for ES module live bindings this
// would crash at init. Since refreshMockupImages is only CALLED inside
// click handlers (post-init), the circular import is safe. Without this
// import, the `typeof refreshMockupImages === 'function'` bare-ref checks
// throughout this file return 'undefined' in v2-only mode (monolith not
// loaded), silently skipping every modal slider image refresh on size/
// color click — selectors fire, state mutates, analytics tracks, but the
// visible image NEVER updates. Root cause of user-reported bugs #2 + #3.
// 2026-05-19 — Audit-found bug: these imports pointed at the wc7-era
// unversioned files (`wc7-result.js`, `wc-pdp-swap.js`). When the wc14→wc22
// chain renamed every other file, these two imports were forgotten. The
// effect was catastrophic: the OLD `wc-pdp-swap.js` got loaded alongside
// the new wc22 chain, and inside it `applyCleanBreakNow` calls
// `updatePdpVariantState` resolved from the OLD `wc7-selectors.js` —
// which still had the original 2-branch bug (canvas OR paint_kit, no
// canvas_framed). Live trap evidence: every PDP size click produced two
// form-id writes, the wc22 correct write followed milliseconds later by
// the wc7 buggy overwrite via `wc-pdp-swap.js:applyCleanBreakNow`. That's
// why operator's 16x20 framed kept landing on the paint-kit variant
// (45636380950630, paint_kit 16x20_NoFrame) even with my updatePdpVariantState
// fix in place. Repointing these imports to the wc22-versioned files
// keeps everything within one module graph.
import { refreshMockupImages } from './wc77-result.js';
import {
  refreshReopenPill,
  reopenLastResult,
  applyCleanBreakNow,
  applyCleanBreakDeferred,
  flushDeferredSwap,
  minimizeModalToPill,
} from './wc-pdp-swap-77.js';



// ─── Pricing / variant constants ───────────────────────────────────────────

  var CANVAS_PRICING = {
    '8x10':  { sale: 47.90, compare: 59.97 },
    '16x20': { sale: 79.97, compare: 99.97 },
    '24x30': { sale: 119.97, compare: 149.97 },
  };
  // Canvas Framed pricing — premium tier, mirrors orchestrator's CANVAS_FRAMED_VARIANTS
  // price_cents + CANVAS_FRAMED_COMPARE_AT_BY_SIZE. Same price across all 4 colors.
  var CANVAS_FRAMED_PRICING = {
    '8x10':  { sale: 74.97,  compare: 93.97 },
    '16x20': { sale: 114.97, compare: 144.97 },
    '24x30': { sale: 159.97, compare: 186.97 }, /* 2026-05-26 — was 179.97/224.97. Operator price reset for canvas_framed 24x30. */
  };
  // Frame color hex chips. Tweak after operator approves against Printify photography.
  var CANVAS_FRAMED_COLORS = ['White', 'Black', 'Natural', 'Espresso'];
  var CANVAS_FRAMED_COLOR_HEX = {
    'White':    '#FAF8F2',
    'Black':    '#1A1A1A',
    'Natural':  '#C8A77B',
    'Espresso': '#3F2D20',
  };
  var PAINT_KIT_PRICING = {
    '16x20': {
      'No Frame':   { sale: 49.97, compare: 61.97 },
      'DIY Frame':  { sale: 59.97, compare: 74.97 },
      'Pre-Framed': { sale: 69.97, compare: 86.97 },
    },
    '20x26': {
      'No Frame':   { sale: 64.97, compare: 80.97 },
      'DIY Frame':  { sale: 74.97, compare: 93.97 },
      'Pre-Framed': { sale: 84.97, compare: 105.97 },
    },
    '24x30': {
      'No Frame':   { sale: 79.97, compare: 99.97 },
      'DIY Frame':  { sale: 89.97, compare: 111.97 },
      'Pre-Framed': { sale: 99.97, compare: 124.97 },
    },
  };
  var PAINT_KIT_CANVAS_LABEL = {
    '16x20': '16″ × 20″ superior linen canvas',
    '20x26': '20″ × 26″ superior linen canvas',
    '24x30': '24″ × 30″ superior linen canvas',
  };
  // Maps the user-visible frame label to the variant key the orchestrator returned.
  var FRAME_TO_VARIANT_KEY = {
    'No Frame':   'NoFrame',
    'DIY Frame':  'DIYFrame',
    'Pre-Framed': 'Pre-Framed',
  };

// ─── Format a price like "$42.97" ─────────────────────────────────────────

  function fmtPrice(n) {
    if (typeof n !== 'number') return '';
    return '$' + n.toFixed(2);
  }

  // Compute the percent-off badge text (e.g. "-20%") given sale + compare
  function fmtSavings(sale, compare) {
    if (!sale || !compare || compare <= sale) return '';
    var pct = Math.round(((compare - sale) / compare) * 100);
    return '-' + pct + '%';
  }

  function getCurrentPricing() {
    var R = window.WC._currentResult;
    if (R.activeFormat === 'canvas') {
      return CANVAS_PRICING[R.activeCanvasSize] || null;
    }
    if (R.activeFormat === 'canvas_framed') {
      return CANVAS_FRAMED_PRICING[R.activeCanvasFramedSize] || null;
    }
    var bySize = PAINT_KIT_PRICING[R.activePaintKitSize] || {};
    return bySize[R.activePaintKitFrame] || null;
  }

  function getCurrentVariantId() {
    var R = window.WC._currentResult;
    if (R.activeFormat === 'canvas') {
      var c = R.canvasShopify && R.canvasShopify[R.activeCanvasSize];
      return c && c.shopifyVariantId ? c.shopifyVariantId : null;
    }
    if (R.activeFormat === 'canvas_framed') {
      var f = R.canvasFramedShopify && R.canvasFramedShopify[R.activeCanvasFramedSize];
      return f && f.variantByColor && f.variantByColor[R.activeCanvasFramedColor]
        ? f.variantByColor[R.activeCanvasFramedColor]
        : null;
    }
    var key = R.activePaintKitSize + '_' + (FRAME_TO_VARIANT_KEY[R.activePaintKitFrame] || R.activePaintKitFrame);
    return (R.paintKitShopify && R.paintKitShopify.variants && R.paintKitShopify.variants[key]) || null;
  }

  function refreshPricingDisplay() {
    var p = getCurrentPricing();
    // ── 1. Modal pricing (existing behavior). Modal might not be in the
    //    DOM on a parent-PDP page that hasn't opened the customize UI
    //    yet — that's fine, the size/frame pickers sitting on the PDP
    //    still need their price updated.
    var modal = getModal();
    if (modal) {
      var compEl = modal.querySelector('[data-wc-element="price-compare"]');
      var saleEl = modal.querySelector('[data-wc-element="price-sale"]');
      var savEl  = modal.querySelector('[data-wc-element="price-savings"]');
      if (p) {
        if (compEl) compEl.textContent = fmtPrice(p.compare);
        if (saleEl) saleEl.textContent = fmtPrice(p.sale);
        if (savEl)  savEl.textContent  = fmtSavings(p.sale, p.compare);
      } else {
        if (compEl) compEl.textContent = '';
        if (saleEl) saleEl.textContent = '';
        if (savEl)  savEl.textContent  = '';
      }
    }
    // ── 2. PDP <product-price> lipstick. On Canvas Print + PBN parent
    //    PDPs the underlying Shopify variants all carry the same
    //    placeholder price (because no one buys the parent — they
    //    customize and the orchestrator-created child has the real
    //    pricing). Override the displayed price text to match what the
    //    customer would actually pay for the size+frame combo they're
    //    eyeing. Pure UI lipstick — no variant data is mutated.
    _updatePdpProductPrice(p);
  }

  // Update every <product-price data-product-id="X"> on the page that
  // matches the main PDP's product ID. Replaces the text inside the
  // .price span (sale) and .compare-at-price span (strikethrough),
  // preserving Shopify's surrounding markup so dynamic-checkout +
  // accelerated-checkout components keep working.
  function _updatePdpProductPrice(p) {
    if (!p) return;
    var anchor = document.querySelector('product-form-component[data-product-id]')
              || document.querySelector('product-price[data-product-id]');
    if (!anchor) return;
    var pid = anchor.getAttribute('data-product-id');
    if (!pid) return;
    var nodes = document.querySelectorAll('product-price[data-product-id="' + pid + '"]');
    for (var i = 0; i < nodes.length; i++) {
      var pp = nodes[i];
      // Sale price: the .price span (Horizon decorates with extra
      // utility classes like price-font-weight-XYZ; we match on the
      // canonical .price class only).
      var saleSpans = pp.querySelectorAll('.price');
      for (var j = 0; j < saleSpans.length; j++) {
        // Skip ancestor containers that themselves carry .price but
        // hold child .price spans — only replace if the element has
        // no element children (i.e. it's a leaf text holder).
        if (saleSpans[j].children.length === 0) {
          saleSpans[j].textContent = fmtPrice(p.sale);
        }
      }
      var compareSpans = pp.querySelectorAll('.compare-at-price');
      for (var k = 0; k < compareSpans.length; k++) {
        if (compareSpans[k].children.length === 0) {
          compareSpans[k].textContent = fmtPrice(p.compare);
        }
      }
    }
  }

// ─── Phase A — selection-state surfaces ───────────────────────────────────
// Modal AND PDP-options share the same data-wc-action / data-wc-format /
// data-wc-size / data-wc-frame contracts, so the refresh helpers fan out
// across both.

  function _selectionScopes() {
    return document.querySelectorAll(
      '[data-wc-component="modal"], [data-wc-component="pdp-options"]'
    );
  }

  // 2026-07-02 QA round-3 (round-1 #14) — co-resident picker copies.
  // The pdp-options snippet renders with FIXED radio group names
  // ("wc-pdp-canvas-size", "wc-pdp-canvas-framed-color", …). When two or
  // more copies co-reside on one page (buy-buttons copy + a flow-b/flow-c
  // copy — they coexist at least until wc77-init's subtree removal, and in
  // the theme editor permanently), the browser treats same-name radios as
  // ONE document-wide group: checking a pill in the visible copy silently
  // UNCHECKS its counterpart in the other copy (and vice versa), leaving
  // whichever copy lost the race with radio.checked=false under an
  // is-checked painted label — a stale, out-of-sync DOM state that anything
  // reading :checked (native form logic, CSS :has) can trip over.
  // Fix: suffix every radio group name in every scope AFTER the first with
  // a per-copy marker so each copy owns its own radio groups. Cheap +
  // idempotent, and swept from the shared refresh chain (not just init)
  // because the Version D projection injects new base-named radios into
  // every copy after boot. Selection state itself is driven by
  // data-wc-action delegation + R, never by these names, so renaming is
  // behavior-neutral everywhere else.
  function _dedupeRadioGroupNames() {
    var scopes = _selectionScopes();
    if (scopes.length < 2) return;
    for (var i = 1; i < scopes.length; i++) {
      var suffix = '--wc-copy-' + i;
      scopes[i].querySelectorAll('input[type="radio"][name]').forEach(function (r) {
        var n = r.getAttribute('name') || '';
        if (n && n.indexOf('--wc-copy-') === -1) r.setAttribute('name', n + suffix);
      });
    }
  }

  // Phase A v1.8.1 — _setSelected: paints both checked AND unchecked
  // labels with explicit inline styles. ALL unchecked labels paint the
  // same way (regardless of whether they were previously selected) so
  // there's no visual divergence between "plain unchecked" and "just
  // unchecked." Inline !important wins all specificity battles. We also
  // blur the radio on un-check so the browser's focus ring doesn't make
  // the just-unchecked label look heavier than its peers.
  function _setSelected(el, on) {
    if (!el) return;
    if (el.tagName === 'INPUT' && el.type === 'radio') {
      if (el.checked !== on) el.checked = on;
      var lbl = el.closest('label');
      if (lbl) {
        lbl.classList.toggle('is-checked', !!on);
        var txt = lbl.querySelector('.variant-option__button-label__text');
        if (on) {
          lbl.style.setProperty('background-color', 'var(--color-foreground)', 'important');
          lbl.style.setProperty('border-color', 'var(--color-foreground)', 'important');
          lbl.style.setProperty('outline', 'none', 'important');
          lbl.style.setProperty('box-shadow', 'none', 'important');
          if (txt) txt.style.setProperty('color', 'var(--color-background)', 'important');
        } else {
          // Always paint unchecked labels with the same explicit inline
          // styles — never rely on CSS cascade defaults for the border.
          // That way the just-unchecked label looks identical to the
          // plain unchecked labels next to it.
          lbl.style.setProperty('background-color', '#fff', 'important');
          lbl.style.setProperty('border-color', 'var(--color-foreground)', 'important');
          lbl.style.setProperty('outline', 'none', 'important');
          lbl.style.setProperty('box-shadow', 'none', 'important');
          if (txt) txt.style.setProperty('color', 'var(--color-foreground)', 'important');
          // Browser focus ring on the just-unchecked radio can render as
          // a heavier outline on the label — blur it explicitly.
          if (document.activeElement === el) {
            try { el.blur(); } catch (e) { /* ignore */ }
          }
        }
      }
    } else {
      el.setAttribute('aria-checked', on ? 'true' : 'false');
      if (el.classList && el.classList.contains('wolden-customize__format-tile')) {
        el.classList.toggle('wolden-customize__format-tile--active', on);
        el.setAttribute('aria-selected', on ? 'true' : 'false');
      }
    }
  }

  function refreshActiveBranches() {
    var R = window.WC._currentResult;
    if (!R) return;
    // Round-1 #14 — sweep BEFORE painting so no copy's paint gets undone by
    // a cross-copy radio-group uncheck (see _dedupeRadioGroupNames).
    try { _dedupeRadioGroupNames(); } catch (e) { /* non-fatal */ }
    _selectionScopes().forEach(function (scope) {
      // Format selectors — modal uses <button class="format-tile">, PDP uses <input type=radio>
      scope.querySelectorAll('[data-wc-action="select-format"]').forEach(function (el) {
        var on = el.getAttribute('data-wc-format') === R.activeFormat;
        _setSelected(el, on);
      });
      // Branched content (per-format <div>) + bottom thumbnails
      scope.querySelectorAll('[data-wc-format-branch]').forEach(function (branch) {
        branch.hidden = branch.getAttribute('data-wc-format-branch') !== R.activeFormat;
      });
    });
    // Phase A v1.4 — paint format display value in PDP legend
    var formatLabel = R.activeFormat === 'canvas' ? 'Canvas Print' : 'Paint Kit';
    document.querySelectorAll('[data-wc-element="format-display"]').forEach(function (el) {
      el.textContent = formatLabel;
    });
  }

  function refreshSizeFramePills() {
    var R = window.WC._currentResult;
    if (!R) return;
    // Round-1 #14 — sweep BEFORE painting so no copy's paint gets undone by
    // a cross-copy radio-group uncheck (see _dedupeRadioGroupNames).
    try { _dedupeRadioGroupNames(); } catch (e) { /* non-fatal */ }
    _selectionScopes().forEach(function (scope) {
      scope.querySelectorAll('[data-wc-action="select-canvas-size"]').forEach(function (el) {
        _setSelected(el, el.getAttribute('data-wc-size') === R.activeCanvasSize);
      });
      scope.querySelectorAll('[data-wc-action="select-paintkit-size"]').forEach(function (el) {
        _setSelected(el, el.getAttribute('data-wc-size') === R.activePaintKitSize);
      });
      scope.querySelectorAll('[data-wc-action="select-paintkit-frame"]').forEach(function (el) {
        _setSelected(el, el.getAttribute('data-wc-frame') === R.activePaintKitFrame);
      });
      // Canvas Framed — size pills
      scope.querySelectorAll('[data-wc-action="select-canvas-framed-size"]').forEach(function (el) {
        _setSelected(el, el.getAttribute('data-wc-size') === R.activeCanvasFramedSize);
      });
      // Canvas Framed — color swatches
      scope.querySelectorAll('[data-wc-action="select-canvas-framed-color"]').forEach(function (el) {
        _setSelected(el, el.getAttribute('data-wc-color') === R.activeCanvasFramedColor);
      });
    });
    // Phase A v1.4 — paint legend display values across all surfaces.
    document.querySelectorAll('[data-wc-element$="canvas-size-display"]').forEach(function (el) {
      // Skip framed-specific display nodes (they have their own selector below).
      if (!el.getAttribute('data-wc-element').includes('framed')) {
        el.textContent = R.activeCanvasSize;
      }
    });
    document.querySelectorAll('[data-wc-element$="paintkit-size-display"]').forEach(function (el) {
      el.textContent = R.activePaintKitSize;
    });
    document.querySelectorAll('[data-wc-element$="paintkit-frame-display"]').forEach(function (el) {
      el.textContent = R.activePaintKitFrame;
    });
    // Canvas Framed display values.
    document.querySelectorAll('[data-wc-element$="canvas-framed-size-display"]').forEach(function (el) {
      el.textContent = R.activeCanvasFramedSize;
    });
    document.querySelectorAll('[data-wc-element$="canvas-framed-color-display"]').forEach(function (el) {
      el.textContent = R.activeCanvasFramedColor;
    });
    // 2026-05-14 v4 — Deterministic visual-state class for the framed
    // color swatches. Horizon's product-info component overwrites the
    // radio :checked state asynchronously, so CSS `:has(:checked)` ends
    // up on whatever variant matched the URL, not the clicked color.
    // We control wc-swatch-active ourselves — exactly one label per
    // picker group gets it, matching R.activeCanvasFramedColor.
    document.querySelectorAll('input[type="radio"][data-wc-action="select-canvas-framed-color"]').forEach(function (inp) {
      var lbl = inp.closest('label.variant-option__button-label--has-swatch');
      if (lbl) {
        var match = inp.getAttribute('data-wc-color') === R.activeCanvasFramedColor;
        lbl.classList.toggle('wc-swatch-active', match);
      }
    });

    // 2026-05-14 v5 — Whenever the active format is NOT canvas_framed,
    // restore any slides + thumbs we hid (display:none) as part of the
    // framed 3-slot model. Without this, the customer switches from
    // framed → paint_kit and ends up with a half-empty gallery. We do
    // this in refreshSizeFramePills (which runs on every state change)
    // rather than only in the format-change handler so it's robust to
    // any code path that flips activeFormat.
    if (R.activeFormat !== 'canvas_framed' && typeof restorePdpGalleryVisibility === 'function') {
      restorePdpGalleryVisibility();
    }
    // Sync the DIY-frame upsell checkbox (modal-only — no PDP equivalent).
    var modal = getModal();
    var diyCheckboxSync = modal && modal.querySelector('[data-wc-element="diy-frame-checkbox"]');
    if (diyCheckboxSync) {
      var shouldCheck = R.activePaintKitFrame === 'DIY Frame';
      if (diyCheckboxSync.checked !== shouldCheck) diyCheckboxSync.checked = shouldCheck;
    }

    // Framed gallery mapping: when on canvas_framed format, the hero image
    // and PDP gallery should show the mockup matching the active (size, color).
    // Front = primary (gallery slot 0), Context = secondary (gallery slot 1).
    try { applyFramedMockupToGallery(); } catch (e) { /* non-fatal */ }
  }

  // Map (size, color) → { front, context } URLs from the 24 framed mockups
  // produced by wolden-framed-mockup-gen. Drives the modal hero image and
  // the PDP gallery for the Framed Canvas format. Called whenever the
  // size or color selection changes, or when fresh mockups land.
  function lookupFramedMockup(size, color) {
    var R = window.WC._currentResult;
    if (!R || !R.canvasFramedMockups) return null;
    var m = R.canvasFramedMockups;
    var front   = m[`Front_${size}_${color}`]   || null;
    var context = m[`Context_${size}_${color}`] || null;
    if (!front && !context) return null;
    return { front: front, context: context };
  }

  // 2026-05-14 v3 — Canvas-framed PDP gallery driver. Replaces the v1/v2
  // ad-hoc applyFramedMockupToGallery (kept as an alias below for the
  // existing call sites).
  //
  // The gallery model for the canvas_framed format is exactly 3 slots:
  //   slot 0 = Main art (R.artDisplayUrl) — always shown, no size/color variance
  //   slot 1 = Front mockup for current (size, color) — auto-active when
  //            customer is on the framed format (so they land on the
  //            in-frame composite, not the bare art)
  //   slot 2 = Context mockup for current (size, color) — same in-room
  //            scene with the customer's art on the canvas
  //
  // For the PDP gallery, we manipulate Horizon's <slideshow-component>
  // and the thumbnail strip in <scroll-hint
  // class="slideshow-controls__thumbnails-container">. The product needs
  // to have at least 3 images attached for the 3rd slide to be present
  // in the DOM — Phase 1.5 in the orchestrator now attaches main art
  // + default-Front + default-Context (back-filled for the operator's
  // QA gens, baked in for fresh gens).
  //
  // The fn is also robust against Horizon's variant-image swap that fires
  // on `variant:changed` (e.g. when our updatePdpVariantState posts a new
  // variant id). We multi-shot + observe (see installFramedGallerySrcGuard
  // below) so the framed mockup pin survives all races.
  function renderCanvasFramedPdpGallery() {
    var R = window.WC._currentResult;
    if (!R || R.activeFormat !== 'canvas_framed') return;
    if (!R.canvasFramedMockups) return; // mockups not ready yet

    var size  = R.activeCanvasFramedSize  || '16x20';
    var color = R.activeCanvasFramedColor || 'White';
    var frontUrl   = R.canvasFramedMockups['Front_'   + size + '_' + color];
    var contextUrl = R.canvasFramedMockups['Context_' + size + '_' + color];
    if (!frontUrl && !contextUrl) return;

    // Slot model — drives both the modal hero and the PDP slideshow.
    var slots = [
      { url: R.artDisplayUrl, label: 'Finished art' },                  // 0
      { url: frontUrl,        label: 'Framed canvas — ' + color },     // 1 (auto-active)
      { url: contextUrl,      label: 'Framed canvas in room' },        // 2
    ];

    function doApply() {
      // ── 1. Modal hero image (legacy fallback for any code that still
      //       reads the result-image element). The modal's main viewer is
      //       now the canvas_framed slider deck, which refreshMockupImages
      //       drives separately. This still pins the result-image as a
      //       belt-and-suspenders.
      var modal = getModal();
      if (modal) {
        var heroImg = modal.querySelector('[data-wc-element="result-image"]');
        if (heroImg && slots[1].url && heroImg.src !== slots[1].url) heroImg.src = slots[1].url;
      }

      // ── 2. PDP slideshow slides. Horizon renders one
      //       <slideshow-slide class="product-media-container"> per
      //       Shopify product image. We read the first N (where N = the
      //       smaller of slide count and slot count), set each img.src
      //       to slots[i].url, and clear srcset so the browser doesn't
      //       override us with a responsive variant of the previous url.
      //
      //       Any slides BEYOND slot 2 are leftover Shopify images
      //       (from messy backfills or theme attachments) — hide them
      //       so the 3-slot model is the customer-facing reality.
      var pdpSlides = document.querySelectorAll('slideshow-slide.product-media-container');
      for (var i = 0; i < pdpSlides.length; i++) {
        if (i < slots.length) {
          if (!slots[i].url) continue;
          var img = pdpSlides[i].querySelector('img');
          if (!img) continue;
          if (img.src !== slots[i].url) {
            img.src = slots[i].url;
            img.srcset = '';
            img.removeAttribute('loading');
          }
          // Make sure we don't accidentally hide the first 3
          pdpSlides[i].style.display = '';
        } else {
          // Hide any leftover/extra slides
          pdpSlides[i].style.display = 'none';
        }
      }

      // ── 3. PDP thumbnail strip. Buttons live in
      //       .slideshow-controls__thumbnails-container; each wraps an
      //       <img> we update to match its slot. We don't touch the
      //       on:click handler (Horizon's slideshow API uses it to swap
      //       the active slide via `/select/<index>`).
      //       Same hide-leftovers treatment for thumbs > slot 2.
      var thumbsContainer = document.querySelector('.slideshow-controls__thumbnails-container');
      if (thumbsContainer) {
        var thumbButtons = thumbsContainer.querySelectorAll(
          'button.slideshow-controls__thumbnail, button.slideshow-control'
        );
        for (var t = 0; t < thumbButtons.length; t++) {
          if (t < slots.length) {
            if (!slots[t].url) continue;
            var timg = thumbButtons[t].querySelector('img');
            if (!timg) continue;
            if (timg.src !== slots[t].url) {
              timg.src = slots[t].url;
              timg.srcset = '';
            }
            thumbButtons[t].style.display = '';
          } else {
            thumbButtons[t].style.display = 'none';
          }
        }
      }

      // ── 4. Other broad-selector gallery imgs (the modal's hero, any
      //       legacy product-media img, any pdp-gallery custom container).
      //       Same slot mapping as the slideshow path; works as a
      //       fallback when the theme renders the gallery differently
      //       (parent PDP that doesn't use slideshow-component, etc.).
      var miscGalleryImgs = document.querySelectorAll(
        '.product__media img, [data-wc-element="pdp-gallery"] img'
      );
      for (var m = 0; m < miscGalleryImgs.length && m < slots.length; m++) {
        if (!slots[m].url) continue;
        if (miscGalleryImgs[m].src !== slots[m].url) {
          miscGalleryImgs[m].src = slots[m].url;
          miscGalleryImgs[m].srcset = '';
        }
      }

      // ── 5. Auto-active slot index on the PDP slideshow. Slot 1 (Front
      //       mockup) is the default when entering framed from another
      //       format. When the customer is staying WITHIN framed (e.g.
      //       changing size or color), we honor WC._framedPreserveSlot
      //       so they don't get yanked back to Front when they were
      //       viewing Context — they just see the new size/color in
      //       whichever slot they were on. renderUnifiedGallery sets
      //       this flag based on the previous format.
      var slideshow = document.querySelector('slideshow-component[ref="slideshow"]');
      if (slideshow && typeof slideshow.select === 'function') {
        try {
          var current = (typeof slideshow.current === 'number') ? slideshow.current : null;
          var wantSlot = (typeof window.WC._framedPreserveSlot === 'number' && window.WC._framedPreserveSlot >= 0 && window.WC._framedPreserveSlot < 3)
            ? window.WC._framedPreserveSlot
            : 1;
          if (current !== wantSlot) slideshow.select(wantSlot);
        } catch (e) { /* slideshow may not be ready */ }
      }
    }

    // Single synchronous apply. No rAF retry — it was causing a visible
    // double-render flicker when the second pass re-set already-correct
    // srcs (the browser re-decodes JPEGs even when the URL is the same
    // since img.src setter doesn't check equality). The MutationObserver
    // guard handles any later overwrites from Horizon's variant:changed.
    doApply();
  }

  // 2026-05-14 — Preload all 24 framed mockup images as soon as they're
  // hydrated into R.canvasFramedMockups, so subsequent (size, color)
  // selections render instantly from cache instead of waiting on a
  // network fetch for each newly-requested mockup. Fired once per
  // mockups-payload via WC._framedMockupsPreloaded set keyed by gen-id.
  window.WC._framedMockupsPreloaded = window.WC._framedMockupsPreloaded || {};
  function preloadFramedMockups() {
    var R = window.WC._currentResult;
    if (!R || !R.canvasFramedMockups || !R.generationId) return;
    if (window.WC._framedMockupsPreloaded[R.generationId]) return;
    window.WC._framedMockupsPreloaded[R.generationId] = true;
    var urls = Object.values(R.canvasFramedMockups || {}).filter(Boolean);
    urls.forEach(function (u) {
      var img = new Image();
      img.decoding = 'async';
      img.src = u;
    });
    log('[framed-preload] kicked off ' + urls.length + ' image preloads for gen ' + (R.generationId || '').slice(0, 8));
  }

  // Back-compat alias: existing call sites still call applyFramedMockupToGallery.
  // 2026-05-14 v7 — Route to the unified gallery driver instead of the
  // framed-specific one, so legacy call sites also enforce the slot model
  // across all formats (canvas, paint_kit, canvas_framed).
  function applyFramedMockupToGallery() {
    if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
    else renderCanvasFramedPdpGallery();
  }

  // 2026-05-14 v7 — Holistic slot model for the PDP gallery, computed
  // per active format. This is the single source of truth for what
  // images appear in what slots; renderUnifiedGallery + the gallery
  // src-guard both read from it.
  //
  // Per operator (2026-05-14):
  //   - canvas (Canvas Print) → 2 slots: Main art (1), Room mockup (2,
  //     top-aligned). Default slot on cross-format entry = slot 2.
  //     Size change updates slot 2's src (preserves selected slot).
  //   - canvas_framed (Framed) → 3 slots: Main art (1), Front mockup (2),
  //     Context mockup (3). Default slot = slot 2. Size + color changes
  //     swap all 3 srcs (preserves selected slot).
  //   - paint_kit (PBN) → 3 slots: Main art (1), Paint Kit Box (2),
  //     PBN B&W (3). Default slot = slot 2. Size + frame changes do
  //     NOT affect gallery.
  function buildSlotModelForFormat(R, format) {
    if (!R) return null;
    var art = R.artDisplayUrl || '';
    if (format === 'canvas_framed') {
      var fSize  = R.activeCanvasFramedSize  || '8x10';
      var fColor = R.activeCanvasFramedColor || 'Black';
      var front   = (R.canvasFramedMockups && R.canvasFramedMockups['Front_'   + fSize + '_' + fColor]) || '';
      var context = (R.canvasFramedMockups && R.canvasFramedMockups['Context_' + fSize + '_' + fColor]) || '';
      return {
        format: 'canvas_framed',
        slotCount: 3,
        slots: [
          { src: art,                       objectPosition: '' },
          { src: front   || art,            objectPosition: '' },
          { src: context || front || art,   objectPosition: '' },
        ],
        defaultSlotIndex: 1, // 1-indexed slot 2 = Front mockup
      };
    }
    if (format === 'canvas') {
      var cSize = R.activeCanvasSize || '8x10';
      var room  = (R.canvasMockups && R.canvasMockups[cSize]) || '';
      var straightOn = R.straightOnMockupUrl || '';   // 2026-05-21 — Printify front shot (all sizes share it)
      // 2026-05-21 — child-PDP native-media fallback. The modal gets the straight-on
      // via the V2 fetch-gen poll, but the child-PDP recovery calls the V1 orchestrator
      // (which has no straightOn) and WC_HYDRATE_HINT isn't present there. The straight-on
      // IS a native product image on the canvas child (alt "Canvas straight-on"), so detect
      // it from the product's own media — cache- + orchestrator-agnostic, and inert for any
      // product that doesn't have one (real traffic / pre-feature canvases → stays 2 slots).
      if (!straightOn) {
        try {
          var _sc = getPrimaryGalleryScope();
          var _so = _sc && _sc.querySelector('slideshow-slide.product-media-container img[src*="straight-on"], slideshow-slide.product-media-container img[alt*="straight-on" i]');
          if (_so && _so.src) { straightOn = _so.src; R.straightOnMockupUrl = straightOn; }
        } catch (e) { /* non-fatal */ }
      }
      var slots = [
        { src: art,         objectPosition: '' },
        { src: room || art, objectPosition: 'top' },
      ];
      if (straightOn) slots.push({ src: straightOn, objectPosition: '' });  // slot-3 only when ready
      return {
        format: 'canvas',
        slotCount: slots.length,   // 2 normally, 3 when the straight-on mockup exists
        slots: slots,
        defaultSlotIndex: 1, // 1-indexed slot 2 = Room mockup
      };
    }
    if (format === 'paint_kit') {
      var box = R.paintKitMockup || '';
      // 2026-05-14 v7 — Per operator: PBN is 2 slots (Art + Box). B&W
      // line-art slot dropped so we get consistent 2-slot behavior on
      // both direct-nav PBN pages AND SPA format-swap from canvas
      // (which has only 2 DOM slides natively). Avoids hard-reload
      // on format change.
      return {
        format: 'paint_kit',
        slotCount: 2,
        slots: [
          { src: art,            objectPosition: '' },
          { src: box || art,     objectPosition: 'top' }, // Box mockup top-aligned
        ],
        defaultSlotIndex: 1, // 1-indexed slot 2 = Paint Kit Box
      };
    }
    return null;
  }

  // 2026-05-14 v7 — Paint a slot model into the PDP gallery: slideshow
  // slides, thumbnails, miscellaneous gallery imgs, and the
  // slideshow.select(targetSlot) call. Slides + thumbs beyond
  // model.slotCount are hidden (display:none) so each format presents
  // exactly N slots to the customer.
  function applySlotModelToGallery(model, targetSlot) {
    if (!model || !Array.isArray(model.slots)) return;
    var slots = model.slots;
    var slotCount = model.slotCount;

    // 2026-05-14 v7 (fix) — Scope to the PRIMARY product gallery only.
    // The PDP has 12+ slideshow-component instances (the main gallery
    // + every recommendation card carousel). Iterating all of them
    // hid the recommendation cards' product images. The primary
    // gallery lives in .product-information, NEVER in a product-card.
    var primaryGalleryScope = getPrimaryGalleryScope();
    if (!primaryGalleryScope) return;

    // 1. PDP slideshow slides (scoped to primary)
    var pdpSlides = primaryGalleryScope.querySelectorAll('slideshow-slide.product-media-container');

    // ─── 2026-05-15 — DOM/data mismatch guard ───────────────────────
    // The slot model assumes a fixed number of DOM slides per format:
    //   canvas      → 2 slides (art + size-specific room mockup)
    //   canvas_framed → 3 slides (art + front mockup + context mockup)
    //   paint_kit   → 2 slides (art + paint kit box)
    //
    // If a child product on Shopify has FEWER images than the model
    // expects (e.g., reconciliation failed at create-time and the
    // main art was never attached), pdpSlides.length < slotCount.
    //
    // Without this guard, the loop below paints slots[0].src = art
    // into the only existing slide — which is usually the mockup
    // image — silently REPLACING the mockup with main art. The
    // customer sees the mockup briefly during initial paint, then
    // it gets overwritten by our slot model and the gallery
    // collapses to "main art only" on a single slide. Live-traced
    // 2026-05-15 on /products/custom-canvas-print-8x10-8e9cb9e2
    // (had 1 image where it should have had 2).
    //
    // Fix: BAIL. Leave the native Shopify gallery untouched. The
    // customer at least sees the stored image (mockup) rather than
    // a misleading main-art-only state. Fire a telemetry event so
    // PostHog surfaces every affected product for backfill.
    if (pdpSlides.length < slotCount) {
      log('applySlotModel: DOM mismatch (slides=' + pdpSlides.length + ' < slotCount=' + slotCount + ') — bailing to preserve native gallery', {
        format: model.format,
        slotCount: slotCount,
        pdpSlidesCount: pdpSlides.length,
        path: window.location.pathname,
      });
      try {
        if (typeof track === 'function') {
          track('gallery_dom_mismatch', {
            format: model.format,
            expected_slots: slotCount,
            actual_slides: pdpSlides.length,
            url_path: window.location.pathname,
            generation_id: (window.WC._currentResult && window.WC._currentResult.generationId) || null,
          });
        }
      } catch (e) { /* ignore */ }
      return;
    }
    for (var i = 0; i < pdpSlides.length; i++) {
      if (i < slotCount && slots[i]) {
        var img = pdpSlides[i].querySelector('img');
        if (img) {
          if (slots[i].src && img.src !== slots[i].src) {
            img.src = slots[i].src;
            img.srcset = '';
            img.removeAttribute('loading');
          }
          img.style.objectPosition = slots[i].objectPosition || '';
        }
        pdpSlides[i].style.display = '';
      } else {
        pdpSlides[i].style.display = 'none';
      }
    }

    // 2. PDP thumbnail strip (scoped to primary)
    var thumbsContainer = primaryGalleryScope.querySelector('.slideshow-controls__thumbnails-container');
    if (thumbsContainer) {
      var thumbButtons = thumbsContainer.querySelectorAll(
        'button.slideshow-controls__thumbnail, button.slideshow-control'
      );
      for (var t = 0; t < thumbButtons.length; t++) {
        if (t < slotCount && slots[t]) {
          var timg = thumbButtons[t].querySelector('img');
          if (timg) {
            if (slots[t].src && timg.src !== slots[t].src) {
              timg.src = slots[t].src;
              timg.srcset = '';
            }
            timg.style.objectPosition = slots[t].objectPosition || '';
          }
          thumbButtons[t].style.display = '';
        } else {
          thumbButtons[t].style.display = 'none';
        }
      }
    }

    // 3. Misc legacy gallery imgs (scoped to primary)
    var miscGalleryImgs = primaryGalleryScope.querySelectorAll(
      '.product__media img, [data-wc-element="pdp-gallery"] img'
    );
    for (var m = 0; m < miscGalleryImgs.length && m < slotCount; m++) {
      if (!slots[m] || !slots[m].src) continue;
      if (miscGalleryImgs[m].src !== slots[m].src) {
        miscGalleryImgs[m].src = slots[m].src;
        miscGalleryImgs[m].srcset = '';
      }
      miscGalleryImgs[m].style.objectPosition = slots[m].objectPosition || '';
    }

    // 4. Auto-active target slot on the PRIMARY slideshow
    var slideshow = primaryGalleryScope.querySelector('slideshow-component[ref="slideshow"]');
    if (slideshow && typeof slideshow.select === 'function') {
      var want = (typeof targetSlot === 'number' && targetSlot >= 0 && targetSlot < slotCount)
        ? targetSlot
        : (typeof model.defaultSlotIndex === 'number' ? model.defaultSlotIndex : 0);
      try {
        var cur = (typeof slideshow.current === 'number') ? slideshow.current : null;
        if (cur !== want) slideshow.select(want);
      } catch (e) { /* slideshow may not be ready */ }
    }

    // 5. Slideshow counter denominator (2026-05-19). Horizon's
    //    slideshow-controls.liquid renders "1/{{ item_count }}" where
    //    item_count is the FULL product image set (e.g. 24 for a Framed
    //    child product with 4 colors × 3 sizes × 2 views). Our slot model
    //    only USES the first 2-3 slides; the rest are display:none. So
    //    the counter reads "1/24" when it should read "1/3" (framed) or
    //    "1/2" (canvas / paint_kit). Replace the text node after the
    //    .slash span with our slotCount; Horizon owns the numerator
    //    (<span ref="current">) so we leave that alone.
    try {
      var counter = primaryGalleryScope.querySelector('.slideshow-controls__counter');
      if (counter) {
        var slashEl = counter.querySelector('.slash');
        if (slashEl) {
          var sib = slashEl.nextSibling;
          while (sib) {
            var nx = sib.nextSibling;
            counter.removeChild(sib);
            sib = nx;
          }
          counter.appendChild(document.createTextNode(String(slotCount)));
        }
      }
    } catch (e) { /* counter is theme-controlled markup — best-effort */ }

    // 6. Arrow wrap-around at slotCount boundary (2026-05-19).
    //    Background: Horizon's <slideshow-component> on the product PDP carries
    //    the FULL product image set (e.g. 24 slides for a Framed child product
    //    with 4 colors × 3 sizes × 2 views). The slot model hides every slide
    //    past slotCount via display:none, but the slideshow's `infinite`
    //    attribute still allows .next() to advance to slide 3, 4, 5, ...
    //    Result: clicking the right arrow past slot 3 lands on a hidden
    //    slide, the counter reads "4/3" / "5/3" / etc., and the customer
    //    sees a blank gallery. Same for the left arrow at slot 0 → slide -1
    //    wraps to slide 23, not slot (slotCount-1).
    //
    //    Fix: override .next() / .previous() on the slideshow INSTANCE
    //    (not the prototype — must NOT affect recommendation sliders).
    //    Horizon's declarative `on:click="/next"` ends up calling
    //    slideshow.next() via its event delegation, so an instance-level
    //    method override is the cleanest interception point. An earlier
    //    capture-phase listener attempt double-fired with Horizon's own
    //    document/window-level capture listener (verified live: every
    //    click ran slideshow.select twice — once from Horizon at cur+1,
    //    once from us at the post-Horizon current+1, net jump of 2). The
    //    method override avoids that entirely because Horizon stops at
    //    "call slideshow.next()" rather than calling .select() directly.
    //    Mobile touch-swipe wrap already works natively (Horizon's
    //    scroll-snap → onScroll path is bounded by slide DOM positions;
    //    the hidden slides past slotCount have zero rendered width so the
    //    swipe target naturally collapses back to slot 0).
    try {
      var slideshow6 = primaryGalleryScope.querySelector('slideshow-component[ref="slideshow"]');
      if (slideshow6) {
        slideshow6.__wcSlotCount = slotCount;
        if (!slideshow6.__wcArrowWrapWired) {
          slideshow6.__wcArrowWrapWired = true;
          var _origNext = slideshow6.next.bind(slideshow6);
          var _origPrev = slideshow6.previous.bind(slideshow6);
          slideshow6.next = function (event, options) {
            try {
              if (event && typeof event.preventDefault === 'function') event.preventDefault();
              var slot = slideshow6.__wcSlotCount || 0;
              if (slot <= 1) return _origNext(event, options);
              var cur = (typeof slideshow6.current === 'number') ? slideshow6.current : 0;
              var target = (cur >= slot - 1) ? 0 : cur + 1;
              // Update the persistent slot intent BEFORE selecting. Horizon's
              // placeholder-reorder dance for non-adjacent wraps mutates the
              // gallery DOM, which fires the gallery-guard MutationObserver →
              // renderUnifiedGallery → applySlotModelToGallery(model,
              // _framedPreserveSlot). Without this assignment, that cascade
              // would snap the slideshow back to the previous preserveSlot
              // (verified live: wrap 2→0 ended at slot 1, not 0). Setting
              // preserveSlot first means any re-render lands on our target.
              window.WC._framedPreserveSlot = target;
              if (typeof slideshow6.select === 'function') slideshow6.select(target, event, options);
            } catch (innerErr) {
              return _origNext(event, options);
            }
          };
          slideshow6.previous = function (event, options) {
            try {
              if (event && typeof event.preventDefault === 'function') event.preventDefault();
              var slot = slideshow6.__wcSlotCount || 0;
              if (slot <= 1) return _origPrev(event, options);
              var cur = (typeof slideshow6.current === 'number') ? slideshow6.current : 0;
              var target = (cur <= 0) ? (slot - 1) : (cur - 1);
              // Same rationale as in next() — see comment above.
              window.WC._framedPreserveSlot = target;
              if (typeof slideshow6.select === 'function') slideshow6.select(target, event, options);
            } catch (innerErr) {
              return _origPrev(event, options);
            }
          };
        }
      }
    } catch (e) { /* non-fatal — slideshow may not be ready */ }

    // 7. Native zoom-dialog (Horizon `<zoom-dialog>` in blocks/_product-media-
    //    gallery.liquid) — 2026-05-22. The dialog holds its OWN server-rendered
    //    copy of every product media (thumbnails under [ref="thumbnails"], full
    //    images under ul.dialog-zoomed-gallery > li). applySlotModel only swaps
    //    the visible carousel + thumb strip, so the zoom overlay kept showing the
    //    product's ORIGINAL media (on a parent PDP, the template images). Paint
    //    the dialog's media + thumbnails in lockstep, by the SAME slot index, so
    //    the zoomed image always matches the slot the customer is viewing. The
    //    zoom reads `data_max_resolution` for its high-res swap, so set both.
    try {
      var zoom = primaryGalleryScope.querySelector('zoom-dialog');
      if (zoom) {
        var zMedia = zoom.querySelectorAll('ul.dialog-zoomed-gallery > li');
        for (var z = 0; z < zMedia.length; z++) {
          if (z < slotCount && slots[z] && slots[z].src) {
            var zimg = zMedia[z].querySelector('img.product-media__image') || zMedia[z].querySelector('img');
            if (zimg) {
              if (zimg.src !== slots[z].src) {
                zimg.src = slots[z].src;
                zimg.srcset = '';
                zimg.setAttribute('data_max_resolution', slots[z].src);
                zimg.removeAttribute('loading');
              }
              zimg.style.objectPosition = slots[z].objectPosition || '';
            }
            zMedia[z].style.display = '';
          } else {
            zMedia[z].style.display = 'none';
          }
        }
        var zThumbs = zoom.querySelectorAll('.dialog-thumbnails-list__thumbnail');
        for (var zt = 0; zt < zThumbs.length; zt++) {
          if (zt < slotCount && slots[zt] && slots[zt].src) {
            var ztimg = zThumbs[zt].querySelector('img');
            if (ztimg && ztimg.src !== slots[zt].src) {
              ztimg.src = slots[zt].src;
              ztimg.srcset = '';
            }
            zThumbs[zt].style.display = '';
          } else {
            zThumbs[zt].style.display = 'none';
          }
        }
      }
    } catch (e) { /* zoom-dialog is theme markup — best-effort */ }
  }

  // 2026-05-14 v7 — Resolve the PRIMARY product gallery container so all
  // slot-model queries are scoped to it. The PDP has many other
  // slideshow-component instances (recommendation cards, related products)
  // that share the same custom-element selectors; we must NOT touch them.
  // The primary gallery lives inside `.product-information` and is NEVER
  // inside a `product-card`. Cached on a WeakRef-style stash to avoid
  // re-querying every paint.
  function getPrimaryGalleryScope() {
    // Walk all `.product-information` containers; pick the one that's
    // actually visible (some themes render two — desktop + mobile).
    var candidates = document.querySelectorAll('.product-information');
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      // Must contain the primary slideshow AND not be a product-card.
      if (c.closest('product-card')) continue;
      if (c.querySelector('slideshow-component[ref="slideshow"]')) return c;
    }
    // Fallback — find the slideshow that's NOT inside a product-card.
    var allSlideshows = document.querySelectorAll('slideshow-component[ref="slideshow"]');
    for (var j = 0; j < allSlideshows.length; j++) {
      if (!allSlideshows[j].closest('product-card')) {
        // Return the closest meaningful ancestor (product-information or main).
        return allSlideshows[j].closest('.product-information, [data-section-type="main-product"], main') || allSlideshows[j].parentElement;
      }
    }
    return null;
  }

  // 2026-05-14 v7 — One holistic gallery driver for ALL formats. Every
  // selector change (format, size, color, frame) calls this. It:
  //   1) Computes the slot model for the current (R, format)
  //   2) Decides the target slot:
  //      - Cross-format entry → model.defaultSlotIndex (always slot 2)
  //      - Within-format sub-change → captured slot intent (preserve)
  //   3) Paints slides + thumbs + slideshow.select via applySlotModel
  //
  // Slot preservation rule (per operator 2026-05-14):
  //   - Format change of ANY kind → default slot for the new format
  //   - Within-format sub-change → preserve whichever slot the customer
  //     was on (e.g. Context slot 3 stays on Context after a color
  //     change, just with the new color's Context mockup).
  function renderUnifiedGallery() {
    var R = window.WC._currentResult;
    if (!R || !R.activeFormat) return;
    // 2026-05-14 v8.20 — Gate purely on whether the customer has an
    // active generation. This single condition encodes the actual UX
    // spec: pre-gen state → native catalog gallery (R.generationId
    // null on every fresh page load, so we bail), post-gen state →
    // personalized slot model (R.generationId is set by either a
    // current-session generation or an explicit reopen-pill click).
    //
    // Replaced the prior v8 guard (`wc-auto-child` body class check)
    // because that class is set ONCE at server-render based on URL
    // prefix and never updates client-side. Post-gen on a parent PDP
    // gets the URL replaced via history.replaceState() but the body
    // class lags, so the old guard wrongly blocked renderUnifiedGallery
    // for customers who generated on a parent-PDP URL.
    //
    // Bug A coverage (5-image angel-on-clouds catalog page showing
    // 2/5): on a fresh parent PDP visit R.generationId is null (R is
    // re-initialized on every page load at line 3245, no localStorage
    // auto-hydration), so we bail → native slideshow untouched.
    //
    // Bug B coverage (post-gen-on-parent-PDP selectors don't update
    // gallery): R.generationId is non-null after generation, so we
    // render the slot model regardless of URL/body class state.
    if (!R.generationId) return;
    var nowFmt  = R.activeFormat;
    var prevFmt = window.WC._lastActiveFormat;
    var crossFormat = (prevFmt !== nowFmt);
    window.WC._lastActiveFormat = nowFmt;

    var model = buildSlotModelForFormat(R, nowFmt);
    if (!model) return;

    var targetSlot;
    if (crossFormat) {
      // Format crossover → always go to the format's default slot.
      // Any stale preserveSlot from a previous in-format change is wiped.
      targetSlot = model.defaultSlotIndex;
      window.WC._framedPreserveSlot = targetSlot;
    } else {
      // Same-format sub-change → use the persistent preserveSlot (set
      // by captureFramedSlotIntent at click time, or by a previous
      // renderUnifiedGallery call). The click handler may trigger
      // MULTIPLE renderUnifiedGallery calls in the same tick (via
      // refreshSizeFramePills → applyFramedMockupToGallery, then again
      // explicitly at the end of the handler) — they must ALL land on
      // the same slot. So we don't consume/clear here; the slot stays
      // sticky until the next captureFramedSlotIntent call rewrites it.
      targetSlot = (typeof window.WC._framedPreserveSlot === 'number'
                    && window.WC._framedPreserveSlot >= 0
                    && window.WC._framedPreserveSlot < model.slotCount)
        ? window.WC._framedPreserveSlot
        : model.defaultSlotIndex;
    }

    applySlotModelToGallery(model, targetSlot);
  }

  // Capture the current slideshow slot BEFORE any state mutation in a
  // click handler. Called at the top of every selector handler so we
  // know the customer's "before" slot — renderUnifiedGallery reads it
  // to pin every subsequent render to that slot during this handler.
  // 2026-05-14 v7 (fix) — Writes WC._framedPreserveSlot directly (not
  // an "Intent" that gets consumed). Sticky until next click captures
  // a new value, so multi-render handlers all converge on the same
  // slot. Bound is dynamic — read against the active format's
  // slotCount via buildSlotModelForFormat.
  function captureFramedSlotIntent() {
    var primary = (typeof getPrimaryGalleryScope === 'function') ? getPrimaryGalleryScope() : null;
    var slideshow = primary && primary.querySelector('slideshow-component[ref="slideshow"]');
    var R = window.WC._currentResult;
    var fmt = R && R.activeFormat;
    var model = (R && fmt) ? buildSlotModelForFormat(R, fmt) : null;
    var maxSlot = model ? model.slotCount : 3;
    if (slideshow && typeof slideshow.current === 'number'
        && slideshow.current >= 0 && slideshow.current < maxSlot) {
      window.WC._framedPreserveSlot = slideshow.current;
    } else {
      // Not capturable — leave preserveSlot alone so the previous capture
      // (if any) still applies for downstream renders.
    }
  }

  // 2026-05-14 v7 — Sync a radio group's `.checked` state to a target
  // value. Browser quirk: when preventDefault() is called on a radio
  // click, the native pre-click `.checked = true` toggle is ROLLED BACK
  // at end of event dispatch. A synchronous `.checked = true` set in
  // the click handler gets reverted by that rollback. Running the same
  // set in a microtask (after dispatch) escapes the rollback. We do
  // BOTH passes — sync (so concurrent reads in the same tick see the
  // right state) and microtask (so the final state sticks).
  function syncRadioGroupBy(action, attr, value) {
    var run = function () {
      try {
        document.querySelectorAll('input[type="radio"][data-wc-action="' + action + '"]').forEach(function (r) {
          r.checked = (r.getAttribute(attr) === value);
        });
      } catch (e) { /* non-fatal */ }
    };
    run();
    if (typeof queueMicrotask === 'function') queueMicrotask(run);
    else Promise.resolve().then(run);
  }

  // 2026-05-14 v8 — Cross-fade the primary product gallery during a
  // format change. Cosmetic-only: adds a 200ms opacity fade out, runs
  // the swap callback (which writes new slot srcs via renderUnified-
  // Gallery), then fades back in. Total perceived transition ~400ms —
  // long enough to register a "magical" format swap; short enough not
  // to feel laggy. Only fires on cross-format changes, never on
  // within-format size/color (which preserve slot + only swap one or
  // two imgs).
  function transitionGalleryForFormatChange(swapCallback) {
    var primary = (typeof getPrimaryGalleryScope === 'function') ? getPrimaryGalleryScope() : null;
    if (!primary) { swapCallback(); return; }
    primary.classList.add('wc-gallery-fade-out');
    setTimeout(function () {
      try { swapCallback(); } catch (e) { /* swap should still proceed */ }
      // Tiny extra tick before removing the fade class so the new srcs
      // have a chance to start loading; the CSS transition handles the
      // fade-in.
      setTimeout(function () {
        primary.classList.remove('wc-gallery-fade-out');
      }, 30);
    }, 200);
  }

  // 2026-05-14 v7 — Reset per-format defaults on cross-format entry. Per
  // operator: every format change is a fresh start. Canvas Print →
  // 8x10. Framed → 8x10 + Black. PBN → (no size effect; leave as-is).
  // Also syncs the size/color radios so the UI highlights match.
  function resetFormatDefaults(R, newFormat) {
    if (!R) return;
    if (newFormat === 'canvas') {
      R.activeCanvasSize = '8x10';
      try {
        document.querySelectorAll('input[type="radio"][data-wc-action="select-canvas-size"]').forEach(function (r) {
          r.checked = (r.getAttribute('data-wc-size') === '8x10');
        });
      } catch (e) { /* non-fatal */ }
    } else if (newFormat === 'canvas_framed') {
      R.activeCanvasFramedSize  = '8x10';
      R.activeCanvasFramedColor = 'Black';
      try {
        document.querySelectorAll('input[type="radio"][data-wc-action="select-canvas-framed-size"]').forEach(function (r) {
          r.checked = (r.getAttribute('data-wc-size') === '8x10');
        });
        document.querySelectorAll('input[type="radio"][data-wc-action="select-canvas-framed-color"]').forEach(function (r) {
          r.checked = (r.getAttribute('data-wc-color') === 'Black');
        });
      } catch (e) { /* non-fatal */ }
    }
    // paint_kit: no gallery-affecting defaults to reset
  }

  // 2026-05-14 v5 — When the customer switches AWAY from canvas_framed,
  // restore any slides + thumbs we hid as part of the framed 3-slot
  // model. Without this, paint_kit format ends up with a half-empty
  // gallery (the slides 3+ that held PBN/canvas room mockups remained
  // display:none from the previous framed selection).
  function restorePdpGalleryVisibility() {
    // 2026-05-14 v7 (fix) — scope to primary product gallery only so we
    // never touch recommendation cards' slides/thumbs.
    var primary = (typeof getPrimaryGalleryScope === 'function') ? getPrimaryGalleryScope() : null;
    if (!primary) return;
    primary.querySelectorAll('slideshow-slide.product-media-container').forEach(function (s) {
      if (s.style && s.style.display === 'none') s.style.display = '';
    });
    primary.querySelectorAll('.slideshow-controls__thumbnails-container button').forEach(function (b) {
      if (b.style && b.style.display === 'none') b.style.display = '';
    });
  }

  // 2026-05-14 — Global MutationObserver that re-pins the gallery to our
  // framed mockup whenever (a) we're on canvas_framed format, (b) the
  // mockups are loaded, and (c) something has set the gallery img.src to
  // a URL that doesn't match the current (size, color) pair.
  //
  // Without this, Horizon's native variant-image swap on size-change wins
  // the race even after the setTimeout fallback above — pushState re-renders
  // the product-info section and the new gallery DOM doesn't carry our
  // applied src forward. The observer catches the next img.src write and
  // pins it back to our mockup.
  //
  // Idempotent install — we keep one observer for the lifetime of the page.
  function installFramedGallerySrcGuard() {
    // 2026-05-19 — Bulletproof: observer re-enabled but with a modal-open
    // guard inside the callback. While modal is open, refreshMockupImages
    // writes are EXPECTED (we own them) and the observer should not interfere.
    // After modal-close, applyCleanBreakNow fully resolves the gallery, then
    // observer resumes its job: keep Horizon's late variant-image swap from
    // overwriting our slot model on inline (non-modal) size/color clicks.
    // Round 2 control test (runtime-installed observer + real isTrusted click)
    // showed 7 invocations / 10 corrections / 1.4ms total — non-pathological.
    if (window.WC._framedGalleryGuardInstalled) return;
    window.WC._framedGalleryGuardInstalled = true;
    var slideSelector = 'slideshow-slide.product-media-container img';
    var thumbSelector = '.slideshow-controls__thumbnails-container button img';
    // 2026-05-18 — Re-entrancy cap. Without this, Horizon's native variant-
    // changed handler (which rewrites gallery imgs to whatever variant is
    // currently active on <add-to-cart-component>) and THIS observer
    // (which rewrites them to our slot model) ping-pong each other in a
    // microtask cascade on cross-product format swaps like canvas→paint_kit
    // (where Horizon's "current variant" disagrees with our format target
    // because applyCleanBreakDeferred postpones the URL/variant swap to
    // modal-close). Cap corrections at 5 per img per 1-second sliding
    // window so the loop terminates — after the cap, Horizon's write wins
    // and the user sees the wrong image for one frame, which is far
    // better than a 15-second main-thread freeze + "Page Unresponsive"
    // dialog. Modal-close will then call applyCleanBreakNow which fully
    // resolves the gallery to our slot model.
    var _correctionCounts = new WeakMap();
    var _correctionWindowMs = 1000;
    var _correctionCap = 5;
    var observerCb = function (mutations) {
      // 2026-05-19 — MODAL-OPEN GUARD. While the customize modal is open,
      // refreshMockupImages writes to gallery imgs as part of our own format-
      // change flow. We don't need (and shouldn't) fight those writes — they
      // are us. Bail out and let modal-close's applyCleanBreakNow reconcile.
      // This guard is the surgical replacement for the previous full disable
      // that proved freeze-free across 6 tests, while preserving the
      // observer's role for inline non-modal size/color/frame clicks.
      var _modal = (typeof getModal === 'function') ? getModal() : null;
      if (_modal && !_modal.hidden && _modal.getAttribute('data-wc-modal-state') !== 'hidden') return;
      // 2026-05-14 v7 — Enforce the slot model for the CURRENT format
      // (canvas / canvas_framed / paint_kit), not just framed. The
      // observer's job is to override Horizon's variant-image swap or
      // any other rogue write that doesn't match our slot model.
      // SCOPED to the primary gallery — recommendation cards must not
      // be touched.
      var R = window.WC._currentResult;
      if (!R || !R.activeFormat) return;
      // 2026-05-15 — Bail until hydration completes. Without this guard,
      // the observer fires during initial page paint (Horizon's native
      // gallery loads images), builds a slot model from EMPTY R (no
      // mockups yet), and pins slot 1 to `art` (the fallback) — visibly
      // overwriting the native Shopify-stored mockup with main art. The
      // result was the "main art on every slot" bug on shared-link child
      // PDPs. Matches the v8.20 guard in renderUnifiedGallery.
      if (!R.generationId) return;
      var model = (typeof buildSlotModelForFormat === 'function')
        ? buildSlotModelForFormat(R, R.activeFormat)
        : null;
      if (!model) return;
      var primary = (typeof getPrimaryGalleryScope === 'function') ? getPrimaryGalleryScope() : null;
      if (!primary) return;
      var expected = model.slots.map(function (s) { return (s && s.src) || ''; });
      var slotCount = model.slotCount;

      var anyMatch = false;
      mutations.forEach(function (m) {
        if (m.type !== 'attributes' || m.attributeName !== 'src') return;
        var t = m.target;
        if (!t || !t.matches) return;
        if (!(t.matches(slideSelector) || t.matches(thumbSelector))) return;
        // SKIP mutations on imgs that aren't inside the primary gallery
        // (recommendation cards, related-product carousels, etc.).
        if (!primary.contains(t)) return;
        // Find this img's slot index — sibling lookup is now scoped to
        // the primary gallery only.
        var siblings;
        if (t.matches(slideSelector)) {
          siblings = primary.querySelectorAll(slideSelector);
        } else {
          siblings = primary.querySelectorAll(thumbSelector);
        }
        var idx = Array.prototype.indexOf.call(siblings, t);
        if (idx < 0 || idx >= slotCount) return;
        var want = expected[idx];
        if (want && t.src !== want) {
          // 2026-05-18 — Re-entrancy cap (see install fn comment). Skip the
          // write if we've already corrected this img >= _correctionCap
          // times within the last _correctionWindowMs.
          var c = _correctionCounts.get(t);
          var nowTs = Date.now();
          if (!c || nowTs > c.resetAt) {
            c = { count: 0, resetAt: nowTs + _correctionWindowMs };
          }
          if (c.count >= _correctionCap) {
            // Loop detection — Horizon and we disagree on this img.
            // Give up, let Horizon win, modal-close clean-break will reconcile.
            return;
          }
          c.count++;
          _correctionCounts.set(t, c);
          t.src = want;
          t.srcset = '';
          anyMatch = true;
        }
      });
      // If any pinning happened, also re-assert the slideshow's active
      // slide so Horizon's section re-render doesn't yank the customer
      // back to slot 0. Use captured intent if available; else default.
      if (anyMatch) {
        var slideshow = primary.querySelector('slideshow-component[ref="slideshow"]');
        if (slideshow && typeof slideshow.select === 'function') {
          var wantSlot = (typeof window.WC._framedPreserveSlot === 'number'
                          && window.WC._framedPreserveSlot >= 0
                          && window.WC._framedPreserveSlot < slotCount)
            ? window.WC._framedPreserveSlot
            : model.defaultSlotIndex;
          try {
            var cur = (typeof slideshow.current === 'number') ? slideshow.current : null;
            if (cur !== wantSlot) slideshow.select(wantSlot);
          } catch (e) { /* ignore */ }
        }
      }
    };
    // 2026-05-15 v8 — SCOPE NARROWING. Previously observed `document.body`
    // with `subtree: true`, which meant EVERY src attribute change anywhere
    // on the page (Horizon's slideshow internals, recommendation cards,
    // cart drawer thumbnails, Klaviyo popup imagery, etc.) fired this
    // callback. On a post-gen PDP with the modal open + Horizon's many
    // built-in slideshow components active, that combined with the page's
    // ~150 other MutationObservers can cause measurable main-thread
    // pressure (seen in the field as "page unresponsive" on slower
    // hardware / accumulated tab state).
    //
    // We only care about src changes inside the PRIMARY product gallery.
    // Observe it directly — same subtree filter but rooted at the
    // narrowest possible element. Re-establish if Horizon re-renders the
    // primary section by re-running this function from initAll (idempotent
    // via `_framedGalleryGuardInstalled` flag; but we also re-attach on
    // each render via reattachGuardIfMissing below).
    //
    // If the primary gallery scope isn't found yet (page still painting),
    // fall back to document.body — the callback's `primary.contains(t)`
    // check inside the loop continues to be the correctness guarantee.
    var primaryScope = (typeof getPrimaryGalleryScope === 'function') ? getPrimaryGalleryScope() : null;
    var target = primaryScope || document.body;
    var obs = new MutationObserver(observerCb);
    obs.observe(target, { attributes: true, attributeFilter: ['src'], subtree: true });
    window.WC._framedGalleryGuardObserver = obs;
    window.WC._framedGalleryGuardObserverTarget = target;
    log('[gallery-guard] v8 installed (scope=' + (primaryScope ? 'primary-gallery' : 'document.body-fallback') + ')');

    // 2026-05-19 — Image-enlarge recovery. Horizon's product-image enlarge
    // widget (click on the PDP image to view it large) opens a <dialog> on
    // top of the gallery; when closed, the underlying gallery DOM may have
    // had its IMG elements replaced (not just src-mutated) and our slot
    // model never re-applies. Watch the primary gallery scope for childList
    // changes — when a NEW slideshow-slide IMG appears, re-run
    // renderUnifiedGallery to repaint our slot model on the new nodes.
    // Debounced so the burst of additions Horizon emits on dialog close
    // collapses into a single re-render.
    if (primaryScope) {
      try {
        var _reRenderDebounce = null;
        var galleryNodeObs = new MutationObserver(function (muts) {
          var addedAnyImg = false;
          for (var mi = 0; mi < muts.length && !addedAnyImg; mi++) {
            var added = muts[mi].addedNodes;
            for (var ni = 0; ni < added.length && !addedAnyImg; ni++) {
              var n = added[ni];
              if (!n || n.nodeType !== 1) continue;
              if ((n.tagName === 'IMG') ||
                  (n.querySelector && (n.querySelector('slideshow-slide.product-media-container img') ||
                                       n.querySelector('.slideshow-controls__thumbnails-container button img')))) {
                addedAnyImg = true;
              }
            }
          }
          if (!addedAnyImg) return;
          var _modal = (typeof getModal === 'function') ? getModal() : null;
          if (_modal && !_modal.hidden && _modal.getAttribute('data-wc-modal-state') !== 'hidden') return;
          if (_reRenderDebounce) clearTimeout(_reRenderDebounce);
          _reRenderDebounce = setTimeout(function () {
            _reRenderDebounce = null;
            try {
              if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
              log('[gallery-guard] re-render after image-enlarge / DOM swap');
            } catch (e) { log('[gallery-guard] re-render error: ' + (e && e.message)); }
          }, 50);
        });
        galleryNodeObs.observe(primaryScope, { childList: true, subtree: true });
        window.WC._framedGalleryNodeObserver = galleryNodeObs;
      } catch (e) { log('[gallery-guard] node observer setup failed: ' + (e && e.message)); }
    }

    // 2026-05-15 v8 — Re-bind if the primary gallery container gets
    // replaced by Horizon's section render. We watch the body's childList
    // SHALLOWLY (not deep subtree) for top-level section swaps; cheap.
    if (primaryScope) {
      try {
        var rebindObs = new MutationObserver(function () {
          var newPrimary = (typeof getPrimaryGalleryScope === 'function') ? getPrimaryGalleryScope() : null;
          if (newPrimary && newPrimary !== window.WC._framedGalleryGuardObserverTarget && document.contains(newPrimary)) {
            try { obs.disconnect(); } catch (_) {}
            obs.observe(newPrimary, { attributes: true, attributeFilter: ['src'], subtree: true });
            window.WC._framedGalleryGuardObserverTarget = newPrimary;
            // Also re-run renderUnifiedGallery so our slot model paints into
            // the freshly-swapped DOM right away.
            try { if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery(); } catch (_) {}
            log('[gallery-guard] v8 re-bound to new primary gallery scope');
          }
        });
        // Watch only the immediate parent of the primary gallery for
        // section swaps; if not available, watch <main>. Shallow watch.
        var rebindTarget = primaryScope.parentElement || document.querySelector('main') || document.body;
        rebindObs.observe(rebindTarget, { childList: true });
        window.WC._framedGalleryGuardRebindObserver = rebindObs;
      } catch (e) { log('[gallery-guard] rebind observer setup failed: ' + (e && e.message)); }
    }
  }

  // ─── teardownFramedGallerySrcGuard ─────────────────────────────────────────
  // Disconnects the MutationObservers installed by installFramedGallerySrcGuard.
  // Called by showResult (Sub-project A chunked rewrite) before heavy DOM writes
  // so the observers can't fire into removed/replaced nodes, then
  // installFramedGallerySrcGuard() is called again in idle time to reinstall.
  function teardownFramedGallerySrcGuard() {
    try {
      if (window.WC._framedGalleryGuardObserver) {
        window.WC._framedGalleryGuardObserver.disconnect();
        window.WC._framedGalleryGuardObserver = null;
      }
      if (window.WC._framedGalleryGuardRebindObserver) {
        window.WC._framedGalleryGuardRebindObserver.disconnect();
        window.WC._framedGalleryGuardRebindObserver = null;
      }
      window.WC._framedGalleryGuardInstalled = false;
      window.WC._framedGalleryGuardObserverTarget = null;
    } catch (e) {}
  }

// ─── Phase A v1.1 — variant + price sync ──────────────────────────────────

  // Phase A v1.6 — scope tightly to the main buy-buttons form so we don't
  // hit input[name="id"] / [data-variant-id] on product cards in recommended-
  // products / customer-favorites sections. Those have their own variant
  // pickers and their own forms; touching them caused a measurable slowdown
  // and could mis-route ATC for a customer who clicked a recommended card.
  function _buyButtonsFormScope() {
    // Prefer the product-form-component that lives in buy-buttons.liquid.
    // Fallback to the main product-information container.
    var form = document.querySelector(
      'product-form-component:not([data-section-id*="recommend"]):not([data-section-id*="related"]) form, ' +
      '[data-section-type="main-product"] form[action*="/cart/add"]'
    );
    if (form) return form;
    var pf = document.querySelector('product-form-component');
    if (pf) return pf;
    return document.body;
  }

  function updatePdpVariantState() {
    var R = window.WC._currentResult;
    if (!R || !R.generationId) return;
    // 2026-05-13 — every variant-state change also refreshes the ATC ready
    // state so the button shows "Setting up..." when target variant is null.
    if (typeof refreshAtcReadyState === 'function') {
      // Deferred via microtask so this function's own form writes happen first.
      Promise.resolve().then(refreshAtcReadyState);
    }

    // Resolve variant ID from current state.
    // 2026-05-19 — Bug fix: the original `else` branch handled BOTH
    // canvas_framed AND paint_kit but only resolved paint_kit variants.
    // Result: a child PDP with the format selector on Framed Canvas (any
    // size, any color) would silently stamp the paint-kit variant onto
    // the form's hidden id input + <add-to-cart-component>'s data-variant-id;
    // the customer hit "Add to cart" expecting the $74.97 framed and got
    // the $49 paint kit instead. getCurrentVariantId() at ~line 122
    // already had the correct three-branch resolution; this brings
    // updatePdpVariantState into parity.
    var newVariantId = null;
    var newHandle = null;
    if (R.activeFormat === 'canvas') {
      var c = R.canvasShopify && R.canvasShopify[R.activeCanvasSize];
      newVariantId = c && c.shopifyVariantId ? c.shopifyVariantId : null;
      newHandle = c && c.shopifyHandle ? c.shopifyHandle : null;
    } else if (R.activeFormat === 'canvas_framed') {
      var f = R.canvasFramedShopify && R.canvasFramedShopify[R.activeCanvasFramedSize];
      newVariantId = (f && f.variantByColor && f.variantByColor[R.activeCanvasFramedColor]) || null;
      newHandle = f && f.shopifyHandle ? f.shopifyHandle : null;
    } else {
      var frameKey = FRAME_TO_VARIANT_KEY[R.activePaintKitFrame] || R.activePaintKitFrame;
      var key = R.activePaintKitSize + '_' + frameKey;
      newVariantId = (R.paintKitShopify && R.paintKitShopify.variants && R.paintKitShopify.variants[key]) || null;
      newHandle = (R.paintKitShopify && R.paintKitShopify.shopifyHandle) || null;
    }

    if (newVariantId) {
      var scope = _buyButtonsFormScope();
      // Update the form's hidden variant id input — narrowly scoped.
      scope.querySelectorAll('input[name="id"]').forEach(function (i) {
        i.value = String(newVariantId);
      });
      // Update the add-to-cart / buy-buttons web components in this scope only.
      scope.querySelectorAll('add-to-cart-component, [data-add-to-cart], buy-buttons-component').forEach(function (el) {
        if (el.hasAttribute('data-variant-id')) el.setAttribute('data-variant-id', String(newVariantId));
      });
    }

    // Phase A v1.6 / Q10 — write Wolden cart-line properties as hidden inputs
    // inside the form, so a PDP-native ATC submit (without our modal cart-add
    // path) still gets the same metadata our buildCurrentCartItem attaches.
    if (R.generationId) {
      _writeFormProperty('_generation_id', R.generationId);
      _writeFormProperty('_format', R.activeFormat);
      // Size key per format.
      var sizeForFmt = R.activeFormat === 'canvas' ? R.activeCanvasSize
                     : R.activeFormat === 'canvas_framed' ? R.activeCanvasFramedSize
                     : R.activePaintKitSize;
      _writeFormProperty('_size', sizeForFmt);
      // Frame key per format: paint_kit = 'No Frame'/'DIY Frame'/'Pre-Framed';
      //                      canvas_framed = 'Framed' (the product itself IS framed);
      //                      canvas = '' (no frame).
      var frameForFmt = R.activeFormat === 'paint_kit' ? R.activePaintKitFrame
                      : R.activeFormat === 'canvas_framed' ? 'Framed'
                      : '';
      _writeFormProperty('_frame', frameForFmt);
      // Frame color — only meaningful for canvas_framed.
      _writeFormProperty('_frame_color', R.activeFormat === 'canvas_framed' ? R.activeCanvasFramedColor : '');
      _writeFormProperty('_pet_image_url', R.petImageUrl || '');
      _writeFormProperty('_custom_image_url', R.artDisplayUrl || '');
      _writeFormProperty('_active_handle', newHandle || ''); // Q10
      _writeFormProperty('_canvas_or_pbn_origin', (R.productCtx && R.productCtx.productType) || ''); // Q10
    }

    // Resolve pricing for the new selection and paint the PDP price elements.
    var pricing = null;
    if (R.activeFormat === 'canvas') {
      pricing = CANVAS_PRICING[R.activeCanvasSize] || null;
    } else {
      pricing = ((PAINT_KIT_PRICING[R.activePaintKitSize] || {})[R.activePaintKitFrame]) || null;
    }
    if (pricing) {
      var fmt = function (n) { return '$' + n.toFixed(2); };
      // Phase A v1.6 — scope price selectors to the main product info area
      // (avoid changing prices on recommended-product cards).
      var priceScope = document.querySelector('[data-section-type="main-product"], product-information, main') || document.body;
      priceScope.querySelectorAll(
        '.product-price .price, .product__price, [data-product-price], ' +
        '.price__sale, .price-item--sale, .price-item--regular'
      ).forEach(function (el, i) {
        if (i === 0) el.textContent = fmt(pricing.sale);
      });
      priceScope.querySelectorAll(
        '.price__compare, .price-item--compare, [data-product-compare-price], s.price-item'
      ).forEach(function (el) { el.textContent = fmt(pricing.compare); });
    }
  }

  // Phase A v1.6 / Q10 — set/replace a hidden form input named
  // properties[<key>] inside the buy-buttons form. Used so PDP-native ATC
  // (which uses Shopify's standard form submit) attaches our generation
  // metadata to the cart line via line item properties.
  function _writeFormProperty(key, value) {
    var scope = _buyButtonsFormScope();
    var inputName = 'properties[' + key + ']';
    var input = scope.querySelector('input[name="' + inputName + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      // Append inside the form if scope is a form, otherwise inside scope.
      var form = scope.tagName === 'FORM' ? scope : scope.querySelector('form');
      (form || scope).appendChild(input);
    }
    input.value = String(value || '');
  }

// ─── Modal slider (setSlideIndex / nextSlide / prevSlide) ─────────────────

  window.WC._slider = window.WC._slider || { paint_kit: 1, canvas: 1, canvas_framed: 1 };

  var SLIDE_COUNTS = { paint_kit: 2, canvas: 2, canvas_framed: 3 };

  // 2026-05-22 — Per-format slot count. Canvas is DYNAMIC: it gains a 3rd slot
  // (the straight-on wrapped-canvas shot) only once R.straightOnMockupUrl is set,
  // which arrives async ~10-15s after the art via the worker. Read it LIVE here —
  // not a value cached at first render — so thumbnail clicks + arrow wrap stay
  // correct the moment the 3rd slot appears. (Bugs: 3rd thumb clamped to slide 1,
  // and next-arrow wrapping at slot 2 instead of slot 3.)
  function slideCountFor(format) {
    if (format === 'canvas') {
      var R = window.WC && window.WC._currentResult;
      return (R && R.straightOnMockupUrl) ? 3 : 2;
    }
    return SLIDE_COUNTS[format] || 1;
  }

  function setSlideIndex(format, idx) {
    var modal = getModal();
    if (!modal) return;
    // Key map: format → deck/track/thumbs HTML id suffix.
    var key = format === 'paint_kit' ? 'paint-kit'
            : format === 'canvas_framed' ? 'canvas-framed'
            : 'canvas';
    var track = modal.querySelector('[data-wc-element="slider-track-' + key + '"]');
    var thumbsEl = modal.querySelector('[data-wc-element="slider-thumbs-' + key + '"]');
    if (!track) return;
    var max = slideCountFor(format) - 1;
    idx = Math.max(0, Math.min(max, idx | 0));
    window.WC._slider[format] = idx;
    track.style.transform = 'translateX(-' + (idx * 100) + '%)';
    // Phase 17 — toggle .--active on the matching thumbnail (thumbnails are
    // static markup, not regenerated per render — better perf and lets the
    // <img> srcs persist across slide changes without flicker).
    if (thumbsEl) {
      var thumbs = thumbsEl.querySelectorAll('.wolden-customize__slider-thumb');
      for (var i = 0; i < thumbs.length; i++) {
        thumbs[i].classList.toggle('wolden-customize__slider-thumb--active', i === idx);
        thumbs[i].setAttribute('aria-current', i === idx ? 'true' : 'false');
      }
    }
  }

  // 2026-05-19 — Wrap-around: when at the LAST slide, next loops to first;
  // when at the FIRST slide, prev loops to last. Previously setSlideIndex
  // clamped to [0, max] so the arrows became no-ops at the ends.
  function nextSlide(format) {
    var max = slideCountFor(format) - 1;
    var cur = window.WC._slider[format] || 0;
    var nextIdx = cur >= max ? 0 : cur + 1;
    setSlideIndex(format, nextIdx);
    track('slider_next', { format: format, idx: window.WC._slider[format] });
  }
  function prevSlide(format) {
    var max = slideCountFor(format) - 1;
    var cur = window.WC._slider[format] || 0;
    var prevIdx = cur <= 0 ? max : cur - 1;
    setSlideIndex(format, prevIdx);
    track('slider_prev', { format: format, idx: window.WC._slider[format] });
  }

  // 2026-05-19 — Mobile swipe (< 750px). Listens on the modal for
  // touchstart/touchend within a slider deck and translates a horizontal
  // swipe of >= 50px into next/prev. Vertical-dominant gestures pass
  // through (so native scroll still works inside the modal).
  function initModalSwipeHandlers() {
    var modal = getModal();
    if (!modal || modal.__wcSwipeWired) return;
    modal.__wcSwipeWired = true;
    var SWIPE_THRESHOLD = 50;
    var ACTIVE_BREAKPOINT = 750;
    var sX = null, sY = null, fmt = null;
    modal.addEventListener('touchstart', function (e) {
      if (window.innerWidth >= ACTIVE_BREAKPOINT) return;
      var deck = e.target && e.target.closest && e.target.closest('[data-wc-element^="slider-deck-"]');
      if (!deck) return;
      var t = e.touches && e.touches[0];
      if (!t) return;
      sX = t.clientX; sY = t.clientY;
      var key = (deck.getAttribute('data-wc-element') || '').replace('slider-deck-', '');
      fmt = key === 'paint-kit' ? 'paint_kit'
          : key === 'canvas-framed' ? 'canvas_framed'
          : key === 'canvas' ? 'canvas'
          : null;
    }, { passive: true });
    modal.addEventListener('touchend', function (e) {
      if (sX === null || !fmt) { sX = sY = fmt = null; return; }
      var t = e.changedTouches && e.changedTouches[0];
      if (!t) { sX = sY = fmt = null; return; }
      var dx = t.clientX - sX, dy = t.clientY - sY;
      sX = sY = null;
      var f = fmt; fmt = null;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      if (Math.abs(dx) <= Math.abs(dy)) return;
      if (dx < 0) nextSlide(f); else prevSlide(f);
    }, { passive: true });
  }

  // 2026-05-14 v7 — Canvas modal slider is now 2-slot (Art + active-size
  // mockup). All sizes map to slot 1 (the single secondary). The legacy
  // 4-slide-per-size mapping is gone. Within-format size change should
  // PRESERVE the customer's current slot (Art-or-Mockup), so callers
  // typically don't fire setSlideIndex anymore.
  var CANVAS_SIZE_TO_SLIDE = { '8x10': 1, '16x20': 1, '24x30': 1 };

// ─── Phase A — PDP-options click listener ─────────────────────────────────
// Mirrors the modal's selection handlers for the four selection actions,
// scoped to clicks inside [data-wc-component="pdp-options"].
// Modal + PDP both write to the same WC._currentResult state. Both refresh
// helpers (refreshActiveBranches / refreshSizeFramePills) fan out across
// both surfaces, so changes in one immediately reflect in the other.
//
// Why a separate listener instead of broadening the modal listener:
// the modal listener is bound to the modal element directly
// (modal.addEventListener) so it doesn't hear PDP clicks. Adding a parallel
// document-level listener scoped via target.closest('[data-wc-component="pdp-options"]')
// keeps modal-only actions (close-modal, select-gen, etc.) unaffected.

  function initPdpOptionsListener() {
    if (window.WC.__pdpOptionsListenerWired) return;
    window.WC.__pdpOptionsListenerWired = true;
    document.addEventListener('click', function (e) {
      // 2026-05-17 — Same isTrusted gate as the modal listener; see the
      // detailed comment in initModalSelectorHandlers. Horizon may dispatch
      // synthetic clicks during its variant-changed cascade, which this
      // listener would otherwise treat as real PDP-option clicks and fire
      // the heavy applyCleanBreakNow path — looping into itself.
      if (e.isTrusted === false) return;
      window.__wcDebug = window.__wcDebug || {};
      window.__wcDebug.pdpClicks = (window.__wcDebug.pdpClicks || 0) + 1;
      var pdpRoot = e.target.closest && e.target.closest('[data-wc-component="pdp-options"]');
      if (!pdpRoot) return;
      var trigger = e.target.closest('[data-wc-action]');
      if (!trigger) return;
      // Make sure trigger is inside this PDP root (not a stray modal element).
      if (!pdpRoot.contains(trigger)) return;
      var action = trigger.getAttribute('data-wc-action');
      var R = window.WC._currentResult;
      if (!R) return;

      if (action === 'select-format') {
        // No e.preventDefault — letting the radio toggle natively. Phase A v1.6.
        var f = trigger.getAttribute('data-wc-format');
        if (f && f !== R.activeFormat) {
          R.activeFormat = f;
          // 2026-05-14 v8 — Broadcast format change so the reviews
          // slider (and any future format-reactive components) can
          // re-evaluate their visibility/state.
          try { document.dispatchEvent(new CustomEvent('wc:format-changed', { detail: { format: f, source: 'pdp' } })); } catch (e3) {}
          // 2026-05-14 v7 — Cross-format entry resets size + color defaults
          // (canvas → 8x10, canvas_framed → 8x10 + Black). Must run BEFORE
          // applyCleanBreakNow so the URL handle resolves to the correct
          // child product (e.g. /products/custom-canvas-print-8x10-<hex>).
          if (typeof resetFormatDefaults === 'function') resetFormatDefaults(R, f);
          refreshActiveBranches();
          if (typeof refreshMockupImages === 'function') refreshMockupImages();
          if (typeof refreshPricingDisplay === 'function') refreshPricingDisplay();
          if (typeof refreshSizeFramePills === 'function') refreshSizeFramePills();
          updatePdpVariantState();
          // Phase B — PDP-driven format change → immediate clean break
          // (URL + title + description + JSON-LD all swap; gallery
          // delegated to renderUnifiedGallery below).
          applyCleanBreakNow('pdp-format-change');
          // 2026-05-14 v7 — Modal slider auto-jumps to slot 2 (default).
          if (typeof setSlideIndex === 'function') setSlideIndex(f, 1);
          // 2026-05-14 v8 — Cross-fade the gallery during the format swap
          // so the customer sees a smooth fade-out → swap → fade-in
          // instead of an instant cut. Total ~400ms perceived.
          if (typeof transitionGalleryForFormatChange === 'function') {
            transitionGalleryForFormatChange(function () {
              if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
            });
          } else if (typeof renderUnifiedGallery === 'function') {
            renderUnifiedGallery();
          }
          track('format_toggled', {
            generation_id: R.generationId,
            format: f,
            source: 'pdp',
          });
        }
        return;
      }
      if (action === 'select-canvas-size') {
        var s = trigger.getAttribute('data-wc-size');
        if (!s) return;
        if (s !== R.activeCanvasSize) {
          // 2026-05-14 v7 — Capture pre-mutation PDP slot so within-format
          // size change preserves slot (Slot 1 stays on art; Slot 2 stays
          // on room mockup but updates to new size).
          if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
          // 2026-05-08 — Friend-shared-link path. The customer is on a
          // Custom canvas child PDP (e.g. /products/custom-canvas-
          // print-8x10-867345ad) but LocalStorage doesn't have the
          // matching saved gen — could be a fresh browser, incognito,
          // a friend-shared URL, etc. In that case R.canvasShopify is
          // empty, so updatePdpVariantState() / applyCleanBreakNow()
          // can't resolve the new size's variant ID and the ATC button
          // keeps pointing at the current page's 8x10 variant. Customer
          // clicks 16x20 / 24x30 and the wrong size lands in the cart.
          //
          // The orchestrator names canvas children deterministically
          // (custom-canvas-print-{size}-{8charhex}; same hex across
          // sizes). When we have no saved-gen data, just derive the
          // sibling handle from the URL and navigate. The sibling page
          // loads with its own correct variant + product context. Trades
          // an in-page swap for a full reload but it's the only way to
          // resolve the variant without saved-gen data.
          var hasGenData = !!(
            R.canvasShopify
            && R.canvasShopify[s]
            && R.canvasShopify[s].shopifyVariantId
          );
          var isCustomChild = !!(
            document.body
            && document.body.classList.contains('wc-auto-child')
          );
          if (!hasGenData && isCustomChild) {
            var canvasHandlePattern = /^\/products\/(custom-canvas-print)-(?:8x10|16x20|24x30)-([a-z0-9]+)\/?$/i;
            var m = window.location.pathname.match(canvasHandlePattern);
            if (m) {
              window.location.href = '/products/' + m[1] + '-' + s + '-' + m[2];
              return; // navigation triggered — bail before further state updates
            }
          }

          R.activeCanvasSize = s;
          // 2026-05-08 — Snappy UX. refreshSizeFramePills() paints the
          // black-fill on the clicked pill IMMEDIATELY (cheap DOM
          // toggle). Everything heavier — gallery image swaps, modal
          // slider rebuild, URL/title/description swap, pricing —
          // gets pushed onto the next animation frame so the click
          // feedback never waits on it. Operator reported a "huge
          // lag" when clicking size pills; profiling showed the
          // perceived lag was the synchronous img.src reassignments
          // in _swapGalleryImages + _swapPdpGalleryToCanvasSize
          // running in-line on the same tick as the click handler.
          refreshSizeFramePills();
          var deferSizeWork = function () {
            if (typeof refreshMockupImages === 'function') refreshMockupImages();
            if (typeof refreshPricingDisplay === 'function') refreshPricingDisplay();
            updatePdpVariantState();
            applyCleanBreakNow('pdp-canvas-size-change');
            track('size_selected', {
              generation_id: R.generationId,
              format: 'canvas',
              size: s,
              source: 'pdp',
            });
            // 2026-05-14 v7 — Unified gallery: slot 1 stays art, slot 2
            // updates to the new size's room mockup. Slot preservation
            // honored via captureFramedSlotIntent above.
            if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
          };
          if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(deferSizeWork);
          } else {
            deferSizeWork();
          }
        }
        return;
      }
      if (action === 'select-paintkit-size') {
        e.preventDefault();
        // 2026-05-14 v7 — Per spec: size doesn't change PBN gallery
        // images. Capture slot intent so Horizon's variant:changed
        // (fired by updatePdpVariantState below) can't reset slot.
        if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
        var ps = trigger.getAttribute('data-wc-size');
        if (ps && ps !== R.activePaintKitSize) {
          R.activePaintKitSize = ps;
          if (typeof syncRadioGroupBy === 'function') {
            syncRadioGroupBy('select-paintkit-size', 'data-wc-size', ps);
          }
          refreshSizeFramePills();
          if (typeof refreshMockupImages === 'function') refreshMockupImages();
          if (typeof refreshPricingDisplay === 'function') refreshPricingDisplay();
          updatePdpVariantState();
          // Re-pin slot model — no image change, just enforces that
          // Horizon's variant:changed didn't reset us to slot 0.
          if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
          track('size_selected', {
            generation_id: R.generationId,
            format: 'paint_kit',
            size: ps,
            source: 'pdp',
          });
        }
        return;
      }
      if (action === 'select-paintkit-frame') {
        e.preventDefault();
        if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
        var fr = trigger.getAttribute('data-wc-frame');
        if (fr && fr !== R.activePaintKitFrame) {
          R.activePaintKitFrame = fr;
          if (typeof syncRadioGroupBy === 'function') {
            syncRadioGroupBy('select-paintkit-frame', 'data-wc-frame', fr);
          }
          refreshSizeFramePills();
          if (typeof refreshPricingDisplay === 'function') refreshPricingDisplay();
          updatePdpVariantState();
          // Re-pin slot model (Horizon variant:changed may have reset slot).
          if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
          track('frame_selected', {
            generation_id: R.generationId,
            frame: fr,
            size: R.activePaintKitSize,
            source: 'pdp',
          });
        }
        return;
      }
      if (action === 'select-canvas-framed-size') {
        e.preventDefault();
        // 2026-05-14 v6 — Capture pre-mutation slot for slot-preservation
        // (Horizon's variant:changed resets slideshow.current sync from
        // updatePdpVariantState below; capture now or lose it).
        if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
        var cfs = trigger.getAttribute('data-wc-size');
        if (cfs && cfs !== R.activeCanvasFramedSize) {
          R.activeCanvasFramedSize = cfs;
          // 2026-05-14 v7 — Microtask-safe radio sync to escape the
          // browser's preventDefault-rollback. See syncRadioGroupBy.
          if (typeof syncRadioGroupBy === 'function') {
            syncRadioGroupBy('select-canvas-framed-size', 'data-wc-size', cfs);
          }
          refreshSizeFramePills();
          if (typeof refreshMockupImages === 'function') refreshMockupImages();
          if (typeof refreshPricingDisplay === 'function') refreshPricingDisplay();
          updatePdpVariantState();
          applyCleanBreakNow('pdp-canvas-framed-size-change');
          // 2026-05-14 v7 — Within-format size change PRESERVES slot on
          // both PDP and modal sliders. refreshMockupImages already
          // re-wrote the modal slides with the new size's mockups; we
          // intentionally do NOT call setSlideIndex (which would force
          // the modal back to slot 1 / Front and contradict the spec).
          if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
          track('size_selected', {
            generation_id: R.generationId,
            format: 'canvas_framed',
            size: cfs,
            source: 'pdp',
          });
        }
        return;
      }
      if (action === 'select-canvas-framed-color') {
        e.preventDefault();
        // 2026-05-14 v6 — Capture pre-mutation slot for slot-preservation
        // (see select-canvas-framed-size handler for rationale).
        if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
        var cfc = trigger.getAttribute('data-wc-color');
        if (cfc && cfc !== R.activeCanvasFramedColor) {
          R.activeCanvasFramedColor = cfc;
          // 2026-05-14 v7 — Microtask-safe radio sync to escape the
          // browser's preventDefault-rollback. See syncRadioGroupBy.
          if (typeof syncRadioGroupBy === 'function') {
            syncRadioGroupBy('select-canvas-framed-color', 'data-wc-color', cfc);
          }
          refreshSizeFramePills();
          updatePdpVariantState();
          // 2026-05-14 v7 — Within-format color change PRESERVES slot
          // on both PDP and modal sliders. refreshMockupImages updates
          // the slide srcs to the new color's mockups; we intentionally
          // do NOT call setSlideIndex.
          if (typeof refreshMockupImages === 'function') refreshMockupImages();
          if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
          // No URL change — color is a variant within the same product handle.
          track('frame_color_selected', {
            generation_id: R.generationId,
            format: 'canvas_framed',
            color: cfc,
            source: 'pdp',
          });
        }
        return;
      }
    });
  }

  // 2026-06-13 — ?variant= deep-link size pre-select.
  // Ad / email links land customers on a multi-size product page
  // (e.g. /products/custom-canvas-framed-<hex>?variant=<id>) where the size
  // lives in the Shopify variant, NOT the URL handle — so the path-regex sync
  // in initPdpOptionsState can't see it and the server-rendered pills stay
  // hardcoded to 8x10. Shopify renders the selected variant (the one matching
  // ?variant=) as an embedded <script type="application/json"> block carrying
  // its option values; read size (+ framed color / paint-kit frame) from there
  // so the correct pill highlights with no 8x10 flash. The native ATC form id
  // is already server-set to ?variant=, so checkout is unaffected either way.
  // Returns { size, color, frame } | null.
  function readVariantQspSelection() {
    var vid;
    try { vid = new URLSearchParams(window.location.search).get('variant'); }
    catch (e) { return null; }
    if (!vid) return null;
    var SIZE_RE = /^\d+\s*x\s*\d+$/i;
    var COLORS  = { 'White': 1, 'Black': 1, 'Natural': 1, 'Espresso': 1 };
    var FRAMES  = { 'No Frame': 1, 'DIY Frame': 1, 'Pre-Framed': 1 };
    var scripts = document.querySelectorAll('script[type="application/json"]');
    for (var i = 0; i < scripts.length; i++) {
      var d;
      try { d = JSON.parse(scripts[i].textContent || 'null'); }
      catch (e) { continue; }
      if (!d || typeof d !== 'object' || String(d.id) !== String(vid)) continue;
      var vals = Array.isArray(d.options)
        ? d.options
        : [d.option1, d.option2, d.option3];
      var size = null, color = null, frame = null;
      for (var j = 0; j < vals.length; j++) {
        var o = vals[j] == null ? '' : String(vals[j]);
        if (!size  && SIZE_RE.test(o)) size  = o.replace(/\s+/g, '');
        if (!color && COLORS[o])       color = o;
        if (!frame && FRAMES[o])       frame = o;
      }
      return size ? { size: size, color: color, frame: frame } : null;
    }
    return null;
  }

  // Phase A — PDP options boot-time state seed.
  // Fired from initAll() AFTER initZone has run. Responsibilities:
  //   1. Seed WC._currentResult.activeFormat from the parent's product type
  //      (so a Canvas-source PDP doesn't briefly show PBN pills).
  //   2. Paint initial pill active classes via refresh helpers.
  //   3. Apply visibility (format selector hidden until generationId).
  //   4. For child PDPs without state: hide the entire PDP-options wrapper
  //      (Phase A behavior; Phase C will properly hydrate from localStorage,
  //      and the friend-shared-link UX renders the native variant picker).
  function initPdpOptionsState() {
    var pdpOptions = document.querySelector('[data-wc-component="pdp-options"]');
    if (!pdpOptions) return;
    var R = window.WC._currentResult;
    if (!R) return;

    var sourceFormat = pdpOptions.getAttribute('data-wc-source-format');
    var isChild = pdpOptions.getAttribute('data-wc-is-child') === 'true';
    var hasUploadZone = !!document.querySelector('[data-wc-component="upload-zone"]');

    // Seed activeFormat from the parent's source format if no generation
    // exists yet. (Once generationId is set, the orchestrator path / hydration
    // owns activeFormat.)
    //
    // 2026-05-13 night — added 'canvas_framed' to the whitelist. Without it,
    // a customer hard-refreshing a /products/custom-canvas-framed-* child URL
    // saw R.activeFormat stay at the default 'paint_kit' and the Paint Kit
    // branch (size pills only, no color swatches) render on a Framed Canvas
    // product page — completely wrong format.
    if (!R.generationId && sourceFormat && (sourceFormat === 'canvas' || sourceFormat === 'paint_kit' || sourceFormat === 'canvas_framed')) {
      if (R.activeFormat !== sourceFormat) {
        R.activeFormat = sourceFormat;
      }
    }

    // Phase C — child-PDP hydration. Customer hard-refreshed a child URL
    // (no upload zone, has generation_id metafield). Walk all stored
    // wc.gens.* keys to find a saved gen whose orchestrator output
    // includes this URL's handle. If found, hydrate WC._currentResult
    // and apply clean break (paints title/images/price/variant/desc/JSON-LD
    // for the active size+format).
    if ((isChild || !hasUploadZone) && !R.generationId) {
      var currentHandle = (window.location.pathname.split('/').pop() || '').split('?')[0];
      var match = (window.WC.gens && typeof window.WC.gens.findByChildHandle === 'function')
        ? window.WC.gens.findByChildHandle(currentHandle)
        : null;
      if (match && match.gen) {
        try {
          var snap = match.gen.snapshot || {};
          R.generationId = match.gen.id;
          R.bubbleId = match.gen.bubbleId || null;
          R.artDisplayUrl = match.gen.imageUrl;
          R.petImageUrl = match.gen.petPhotoUrl || R.petImageUrl;
          R.productCtx = R.productCtx || {
            productHandle: pdpOptions.getAttribute('data-wc-product-handle'),
            productType: pdpOptions.getAttribute('data-wc-product-type'),
            artId: null,
          };
          if (snap.canvasMockups)       R.canvasMockups       = snap.canvasMockups;
          if (snap.paintKitMockup)      R.paintKitMockup      = snap.paintKitMockup;
          if (snap.pbnBwUrl)            R.pbnBwUrl            = snap.pbnBwUrl;
          if (snap.pbnColoredUrl)       R.pbnColoredUrl       = snap.pbnColoredUrl;
          if (snap.canvasShopify)       R.canvasShopify       = snap.canvasShopify;
          if (snap.canvasFramedShopify) R.canvasFramedShopify = snap.canvasFramedShopify;
          if (snap.paintKitShopify)     R.paintKitShopify     = snap.paintKitShopify;
          R.activeFormat = match.format;
          if (match.format === 'canvas' && match.size) {
            R.activeCanvasSize = match.size;
          }
          if (match.format === 'canvas_framed') {
            if (match.size)  R.activeCanvasFramedSize  = match.size;
            if (match.color) R.activeCanvasFramedColor = match.color;
          }
          log('child-PDP hydration matched →', currentHandle, '(format', R.activeFormat, ')');
          // Fall through — pdpOptions stays visible, body class added,
          // refresh helpers paint the correct active state.
        } catch (err) {
          log('child-PDP hydration failed', err && err.message);
        }
      } else {
        // 2026-05-13 night — Friend-link / cold-page-load case (no
        // LocalStorage gen matches this child PDP handle). Previously this
        // hid the wolden picker entirely (`pdpOptions.hidden = true`) and
        // showed the native variant picker only. Now we keep our picker
        // visible and bootstrap state from the current page's product JSON
        // (/products/<handle>.js) — so size pills, color swatches, and ATC
        // all work even without any prior LocalStorage. The fetch runs
        // async; the picker shows immediately, ATC enables once variants
        // are merged into state (~100-300ms after page load).
        log('child-PDP hydration no match — bootstrapping from page product JSON');
        if (typeof bootstrapStateFromCurrentChildPdp === 'function') {
          bootstrapStateFromCurrentChildPdp(function (ok) {
            log('[child-PDP bootstrap] result:', ok);
          });
        }
        // Don't return early — let the rest of init run so the picker
        // shows and listens for clicks.
      }
    }
    pdpOptions.hidden = false;

    // Phase A v1.4 — flag the page as Wolden-PDP-active so the CSS rule in
    // wolden-customize.css §Native variant picker hide can suppress the
    // theme's <variant-picker> on customizable parents. Without this hide,
    // the customer would see TWO pickers (theme's + ours), both bound to
    // different sources of truth — confusing.
    // The class is NOT added on cold child PDPs without state (see early
    // return above) so the friend-shared-link case retains the native picker.
    document.body.classList.add('wolden-customize-pdp-active');

    // 2026-05-08 — Sync R.activeCanvasSize / R.activePaintKitSize
    // from the URL handle on Custom child PDPs. Without this, the
    // hardcoded defaults (activeCanvasSize: '8x10', activePaintKit-
    // Size: '16x20') win, and the rendered size pill shows the
    // wrong size selected on, e.g., /products/custom-canvas-print-
    // 24x30-867345ad. The saved-gen hydration path above already
    // sets activeCanvasSize from match.size when it runs, so this
    // is a no-op there; the friend-shared-link path (no saved gen)
    // is what benefits.
    var canvasUrlMatch = window.location.pathname.match(
      /^\/products\/custom-canvas-print-(8x10|16x20|24x30)-[a-z0-9]+\/?$/i
    );
    if (canvasUrlMatch) {
      R.activeCanvasSize = canvasUrlMatch[1];
    }
    // 2026-05-08 — canvas-framed child URL pattern.
    var canvasFramedUrlMatch = window.location.pathname.match(
      /^\/products\/custom-canvas-framed-(8x10|16x20|24x30)-[a-z0-9]+\/?$/i
    );
    if (canvasFramedUrlMatch) {
      R.activeCanvasFramedSize = canvasFramedUrlMatch[1];
    }
    var paintkitUrlMatch = window.location.pathname.match(
      /^\/products\/custom-paint-by-numbers-kit-(8x10|11x14|12x16|16x20|20x26|24x30)-[a-z0-9]+\/?$/i
    );
    if (paintkitUrlMatch) {
      R.activePaintKitSize = paintkitUrlMatch[1];
    }

    // 2026-06-13 — ?variant= deep-link: pre-select the linked variant's size
    // (+ framed color / PBN frame). Highest precedence — overrides the 8x10
    // hardcoded default AND any path-derived size above, so the customer lands
    // on the exact size they were linked to from an ad / email. Multi-size
    // products (custom-canvas-print-<hex>, custom-canvas-framed-<hex>,
    // custom-paint-by-numbers-kit-<hex>) carry size in the variant, not the
    // handle, so the path-regex above never matches them. Format comes from
    // R.activeFormat (seeded above from data-wc-source-format); the embedded
    // selected-variant JSON belongs to the current product = current format.
    try {
      var qspSel = readVariantQspSelection();
      if (qspSel && qspSel.size) {
        if (R.activeFormat === 'canvas') {
          R.activeCanvasSize = qspSel.size;
        } else if (R.activeFormat === 'canvas_framed') {
          R.activeCanvasFramedSize = qspSel.size;
          if (qspSel.color) R.activeCanvasFramedColor = qspSel.color;
        } else if (R.activeFormat === 'paint_kit') {
          R.activePaintKitSize = qspSel.size;
          if (qspSel.frame) R.activePaintKitFrame = qspSel.frame;
        }
      }
    } catch (e) { /* non-fatal — keep existing default */ }

    // 2026-05-13 night — unconditional bootstrap on child PDPs without state.
    // The earlier hydration block was gated on `isChild || !hasUploadZone`
    // which depends on the `custom.generation_id` metafield (often null on
    // older gens, and on framed-canvas children). If we're on a child PDP
    // URL pattern AND have no generationId in state, fetch the current
    // product's JSON and populate variant IDs so ATC works without any
    // prior LocalStorage history. Idempotent — safe to fire even when the
    // hydration block above already populated state.
    var onChildPdp = !!(canvasUrlMatch || canvasFramedUrlMatch || paintkitUrlMatch);
    var stateEmptyForFormat =
      (canvasFramedUrlMatch && (!R.canvasFramedShopify || !R.canvasFramedShopify[canvasFramedUrlMatch[1]]?.variantByColor)) ||
      (canvasUrlMatch       && (!R.canvasShopify       || !R.canvasShopify[canvasUrlMatch[1]]?.shopifyVariantId)) ||
      (paintkitUrlMatch     && (!R.paintKitShopify     || !R.paintKitShopify.variants));
    if (onChildPdp && stateEmptyForFormat && typeof bootstrapStateFromCurrentChildPdp === 'function') {
      bootstrapStateFromCurrentChildPdp(function (ok) {
        log('[child-PDP url-bootstrap] result:', ok);
      });
    }

    // Paint initial state across both modal AND PDP surfaces.
    if (typeof refreshActiveBranches === 'function') refreshActiveBranches();
    if (typeof refreshSizeFramePills === 'function') refreshSizeFramePills();
    // PDP price lipstick — replace the placeholder Shopify variant price
    // ($44.97 across all variants for canvas parents, ditto for PBN
    // parents) with the size+frame-correct price the customer would
    // actually pay post-customize. Pure UI; no variant data is mutated.
    if (typeof refreshPricingDisplay === 'function') refreshPricingDisplay();
    // 2026-05-17 — applyPdpVisibility is a monolith-only function; bare ref
    // throws ReferenceError in v2-only mode, aborting initSelectors mid-init
    // and leaving modal click handlers unwired. typeof guard matches the
    // pattern used by every other monolith bridge call in this file.
    if (typeof applyPdpVisibility === 'function') applyPdpVisibility();
  }

// ─── Modal selector handlers (lifted from modal.addEventListener('click')) ─
// These handler blocks are the selector-specific branches from the monolith's
// large modal click listener (lines ~7744–7935). In wc-init.js, the modal
// click handler should call into these or the initModalSelectorHandlers()
// below can be called to register them as a separate delegated listener.
//
// NOTE: In the monolith these live INSIDE the modal click listener as plain
// if-blocks. For the module, we expose initModalSelectorHandlers() which
// registers its own delegated listener — semantically identical behavior.

  // Round 2 (wc14) instrumentation: step-traced format-change so we can see
  // exactly which sub-call hangs when freeze repro is triggered. Trace is
  // persisted to localStorage on every step so it survives a hard page-freeze
  // — read with window.WC._readDebugTrace() after recovering.
  function _wcRunFormatChange(f, source) {
    var R = window.WC._currentResult;
    if (!R) return { aborted: 'no-currentResult' };
    if (!f || f === R.activeFormat) return { aborted: 'no-change', from: R.activeFormat, to: f };

    var traceKey = 'WC_DEBUG_TRACE_v1';
    var trace = [];
    var lastStep = '<none>';
    function flush() { try { localStorage.setItem(traceKey, JSON.stringify(trace)); } catch (_) {} }
    function step(name, fn) {
      lastStep = name;
      var t0 = performance.now();
      trace.push({ step: name, t: Math.round(t0), status: 'enter' });
      flush();
      try {
        fn();
        var t1 = performance.now();
        trace.push({ step: name, t: Math.round(t1), status: 'ok', dur: Math.round(t1 - t0) });
      } catch (err) {
        var t2 = performance.now();
        trace.push({ step: name, t: Math.round(t2), status: 'err', dur: Math.round(t2 - t0),
                     msg: String(err && err.message || err),
                     stack: String(err && err.stack || '').slice(0, 800) });
      }
      flush();
    }

    try { localStorage.setItem(traceKey, '[]'); } catch (_) {}
    trace.push({ step: 'START', t: Math.round(performance.now()), source: source || 'modal',
                 fromFormat: R.activeFormat, toFormat: f, url: location.pathname });
    flush();

    step('set_activeFormat', function () { R.activeFormat = f; });
    step('dispatch_wc:format-changed', function () {
      document.dispatchEvent(new CustomEvent('wc:format-changed', { detail: { format: f, source: source || 'modal' } }));
    });
    step('resetFormatDefaults', function () {
      if (typeof resetFormatDefaults === 'function') resetFormatDefaults(R, f);
    });
    step('refreshActiveBranches', function () { refreshActiveBranches(); });
    step('refreshSizeFramePills', function () { refreshSizeFramePills(); });
    step('refreshMockupImages', function () {
      if (typeof refreshMockupImages === 'function') refreshMockupImages();
    });
    step('refreshPricingDisplay', function () { refreshPricingDisplay(); });
    step('applyCleanBreakDeferred', function () { applyCleanBreakDeferred('modal-format-change'); });
    step('setSlideIndex', function () { setSlideIndex(f, 1); });
    step('transitionGalleryForFormatChange', function () {
      if (typeof transitionGalleryForFormatChange === 'function') {
        transitionGalleryForFormatChange(function () {
          step('renderUnifiedGallery_via_transition_cb', function () {
            if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
          });
        });
      } else if (typeof renderUnifiedGallery === 'function') {
        step('renderUnifiedGallery_direct', function () { renderUnifiedGallery(); });
      }
    });
    step('track_format_toggled', function () {
      track('format_toggled', { generation_id: R.generationId, format: f, source: source || 'modal' });
    });

    trace.push({ step: 'END', t: Math.round(performance.now()) });
    flush();
    return { ok: true, lastStep: lastStep, traceLength: trace.length };
  }

  // Expose for MCP/console-driven repro (bypasses isTrusted check, lets us
  // fire the exact same code path without needing a visible/clickable tile).
  try {
    window.WC = window.WC || {};
    window.WC._debugRunFormatChange = _wcRunFormatChange;
    window.WC._readDebugTrace = function () {
      try { return JSON.parse(localStorage.getItem('WC_DEBUG_TRACE_v1') || '[]'); } catch (_) { return []; }
    };
    window.WC._clearDebugTrace = function () {
      try { localStorage.removeItem('WC_DEBUG_TRACE_v1'); } catch (_) {}
    };
  } catch (_) {}

  // 2026-05-22 — Modal lightbox (zoom) wiring. The lightbox markup
  // (`[data-wc-component="lightbox"]`, full CSS in wolden-customize.css)
  // is a SIBLING of the modal root, not a descendant — so the modal click
  // delegator can't receive its close button's bubbled clicks. Resolve it
  // at the document level and give it its own close listener. Opening is
  // driven from the modal delegator (the slide is inside the modal).
  function getModalLightbox() { return document.querySelector('[data-wc-component="lightbox"]'); }
  function closeModalLightbox() {
    var lb = getModalLightbox();
    if (lb) lb.hidden = true;
    try { document.body.style.overflow = ''; } catch (_) {}
  }
  function openModalLightbox(src) {
    var lb = getModalLightbox();
    if (!lb || !src) return;
    var img = lb.querySelector('[data-wc-element="lightbox-image"]');
    if (img) { img.src = src; img.srcset = ''; }
    lb.hidden = false;
    try { document.body.style.overflow = 'hidden'; } catch (_) {}
    if (!lb.__wcLightboxWired) {
      lb.__wcLightboxWired = true;
      // Close on the X button (or its children) OR a backdrop click (the
      // lightbox container itself — clicks on the frame/image stay open).
      lb.addEventListener('click', function (ev) {
        if (ev.isTrusted === false) return;
        var t = ev.target;
        if ((t && t.closest && t.closest('[data-wc-action="close-lightbox"]')) || t === lb) {
          ev.preventDefault();
          closeModalLightbox();
        }
      });
    }
  }

  function initModalSelectorHandlers() {
    var modal = getModal();
    if (!modal || modal.__wcSelectorHandlersWired) return;
    modal.__wcSelectorHandlersWired = true;

    // 2026-05-23 — DIY-frame upsell <-> frame-selector lockstep. The "Add DIY
    // Frame" checkbox has no data-wc-action, and the click delegator below also
    // rejects synthetic clicks, so toggling the upsell never moved the frame
    // selection or price (sync was one-way only: frame->checkbox at :377). A real
    // user toggle fires a trusted 'change' here; apply the frame with the SAME
    // internal logic as select-paintkit-frame so radios + price + display +
    // checkbox all move together. (refreshSizeFramePills sets .checked
    // programmatically, which does NOT fire 'change' -> no feedback loop.)
    modal.addEventListener('change', function (e) {
      try {
        var cb = e.target;
        if (!cb || !cb.classList || !cb.classList.contains('wolden-customize__diy-frame-checkbox')) return;
        // 2026-07-02 QA round-1 fix — the Version-D CANVAS frame upsell reuses
        // this class for styling but is a different product decision entirely
        // (canvas vs canvas_framed, owned by the -d click delegator in
        // wc77-experiments.js). Without this bail, checking the canvas upsell
        // ALSO set activePaintKitFrame='DIY Frame', so the Paint Kit branch
        // arrived with an uninvited +$10 DIY Frame preselected.
        if (cb.hasAttribute('data-wc-d-upsell-checkbox')) return;
        var R = window.WC._currentResult;
        if (!R) return;
        var fr = cb.checked ? 'DIY Frame' : 'No Frame';
        if (fr === R.activePaintKitFrame) return;
        if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
        R.activePaintKitFrame = fr;
        if (typeof syncRadioGroupBy === 'function') {
          syncRadioGroupBy('select-paintkit-frame', 'data-wc-frame', fr);
        }
        refreshSizeFramePills();
        refreshPricingDisplay();
        updatePdpVariantState();
        if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
        track('frame_selected', {
          generation_id: R.generationId,
          frame: fr,
          size: R.activePaintKitSize,
          source: 'upsell',
        });
      } catch (ex) { /* swallow — never break the modal */ }
    });

    modal.addEventListener('click', function (e) {
      // 2026-05-17 — Reject synthetic clicks from Horizon's variant-changed
      // cascade. updatePdpVariantState writes to <add-to-cart-component>'s
      // variant attr → Horizon's listener may dispatch synthetic events on
      // form elements which bubble here, re-entering this handler, calling
      // updatePdpVariantState again → infinite loop → "Page Unresponsive".
      // Real user clicks set isTrusted=true; programmatic events from
      // dispatchEvent() set it to false. Filter the synthetic ones out.
      if (e.isTrusted === false) return;
      // Diagnostic counter (visible in DevTools via window.__wcDebug).
      window.__wcDebug = window.__wcDebug || {};
      window.__wcDebug.modalClicks = (window.__wcDebug.modalClicks || 0) + 1;
      var target = e.target;
      while (target && target !== modal) {
        if (!target.getAttribute) { target = target.parentNode; continue; }
        var action = target.getAttribute('data-wc-action');
        if (!action) { target = target.parentNode; continue; }

        if (action === 'select-format') {
          // Phase A v1.6 — letting the radio toggle natively (no preventDefault).
          // wc14 instrumentation: route through _wcRunFormatChange so every
          // sub-call gets timed + persisted to localStorage (so the trace
          // survives a hard page-freeze and can be read after reload).
          var f = target.getAttribute('data-wc-format');
          _wcRunFormatChange(f, 'modal');
          return;
        }
        if (action === 'select-canvas-size') {
          // 2026-05-14 v7 — Capture pre-mutation PDP slot for preservation.
          if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
          var s = target.getAttribute('data-wc-size');
          if (s && s !== window.WC._currentResult.activeCanvasSize) {
            window.WC._currentResult.activeCanvasSize = s;
            refreshSizeFramePills();
            if (typeof refreshMockupImages === 'function') refreshMockupImages();
            refreshPricingDisplay();
            // Phase B — modal-driven canvas size change → defer URL+DOM swap.
            applyCleanBreakDeferred('modal-canvas-size-change');
            // 2026-05-14 v7 — Canvas modal is now 2-slot (Art + active-
            // size Room mockup). Size change PRESERVES current modal
            // slot — refreshMockupImages already populated slot 1 with
            // the new size's mockup. No setSlideIndex call needed.
            // 2026-05-14 v7 — Re-paint PDP gallery now (preserve slot,
            // swap slot-2 room mockup to new size's render).
            if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
            track('size_selected', {
              generation_id: window.WC._currentResult.generationId,
              format: 'canvas',
              size: s,
            });
          }
          return;
        }
        if (action === 'slider-prev' || action === 'slider-next') {
          e.preventDefault();
          var fmt = target.getAttribute('data-wc-format') || window.WC._currentResult.activeFormat;
          if (action === 'slider-prev') prevSlide(fmt); else nextSlide(fmt);
          return;
        }
        if (action === 'slider-goto') {
          e.preventDefault();
          var fmt2 = target.getAttribute('data-wc-format') || window.WC._currentResult.activeFormat;
          var idx = parseInt(target.getAttribute('data-wc-slide'), 10);
          if (!isNaN(idx)) setSlideIndex(fmt2, idx);
          return;
        }
        // 2026-05-22 — Open the lightbox on the CURRENTLY-SHOWN slide image.
        // Reads the clicked slide button's own <img> src, so the zoom always
        // matches whatever the gallery is showing for the active format/slot.
        // Desktop only — mobile keeps native in-place pinch-zoom (operator:
        // mobile modal zoom is out of scope; do not fix).
        if (action === 'zoom-current-slide') {
          e.preventDefault();
          if (window.innerWidth < 750) return;
          var _slideImg = target.querySelector('img');
          var _zsrc = _slideImg && (_slideImg.currentSrc || _slideImg.src);
          // 2026-05-27 — The hero "Finished art" slide now carries the 640px
          // render inline (fast paint). When the customer taps to ZOOM that
          // specific slide, swap in the full 2K render (R.artHighResUrl) so the
          // enlarged view stays crisp. Other slides (mockups) zoom their own
          // src as-is. The hero slides are tagged slide-(pk|canvas|canvas-framed)-art.
          if (_slideImg) {
            var _wel = _slideImg.getAttribute('data-wc-element') || '';
            var _isHero = _wel === 'slide-canvas-art' || _wel === 'slide-pk-art' || _wel === 'slide-canvas-framed-art';
            if (_isHero) {
              var _hi = window.WC && window.WC._currentResult && window.WC._currentResult.artHighResUrl;
              if (_hi) _zsrc = _hi;
            }
          }
          if (_zsrc) openModalLightbox(_zsrc);
          return;
        }
        // 2026-05-17 — Close-modal + back-to-result + view-cart handlers.
        // Monolith had these inside the same click delegate that v2 modules
        // never ported. Adding minimal inline versions here.
        if (action === 'close-modal') {
          e.preventDefault();
          var _modal = getModal();
          var _state = _modal && _modal.getAttribute('data-wc-modal-state');
          if (_state === 'generating') {
            // Minimize to the bottom-right progress-pill instead of hiding
            // outright. The orchestrator fetch keeps running and the customer
            // can reopen the live loader (or the result once it lands). The
            // finished-while-minimized handoff (wc50-result.js) swaps the
            // spinner pill for the "View Your Art" pill automatically.
            try { minimizeModalToPill(); }
            catch (err) { log('minimizeModalToPill error', err && err.message); setModalState('hidden'); }
            return;
          }
          setModalState('hidden');
          try { document.body.style.overflow = ''; } catch (_) {}
          // 2026-05-17 — Fire the deferred PDP swap (URL/title/gallery/desc)
          // now. Queued by applyCleanBreakDeferred from gen-complete + every
          // modal selector click. Single batch swap point — matches monolith
          // pattern (avoids per-click PDP work that caused 15s freezes).
          try { flushDeferredSwap(); } catch (e) { /* non-fatal */ }
          // Refresh reopen pill state — after close it should show "View
          // Your Art" if a stashed result is available.
          try { refreshReopenPill(); } catch (e) { /* non-fatal */ }
          return;
        }
        if (action === 'back-to-result') {
          e.preventDefault();
          setModalState('result');
          return;
        }
        if (action === 'view-cart') {
          e.preventDefault();
          window.location.href = '/cart';
          return;
        }
        if (action === 'continue-customizing' || action === 'restart-upload') {
          e.preventDefault();
          setModalState('hidden');
          try { document.body.style.overflow = ''; } catch (_) {}
          return;
        }
        // 2026-05-17 — ATC handler. Monolith had this at line 7944 calling
        // addCurrentSelectionToCart (~200 LOC). v2-only mode needs a minimal
        // inline version. Skips: liveResolveCurrentVariantId fallback (v2
        // stamps variant IDs reliably), URL rewrite to child handle, DIY
        // frame upsell side-cart. Covers the 95% happy path.
        if (action === 'add-to-cart') {
          e.preventDefault();
          var atcBtn = target;
          var R_atc = window.WC._currentResult;
          if (!R_atc) return;
          var vId = (typeof getCurrentVariantId === 'function') ? getCurrentVariantId() : null;
          if (!vId) {
            // Variant not yet resolved (rare: Phase 1.5 hasn't landed). Friendly retry text.
            var prev0 = atcBtn.firstChild && atcBtn.firstChild.nodeValue;
            if (atcBtn.firstChild) atcBtn.firstChild.nodeValue = ' Variant not ready — try again ';
            setTimeout(function () { if (atcBtn.firstChild && prev0) atcBtn.firstChild.nodeValue = prev0; }, 2500);
            return;
          }
          atcBtn.disabled = true;
          var prevText = atcBtn.firstChild && atcBtn.firstChild.nodeValue;
          if (atcBtn.firstChild) atcBtn.firstChild.nodeValue = ' Adding… ';

          // 2026-05-19 — Fly-to-cart animation on modal ATC click. Mirrors
          // Horizon's product-form.js #animateFlyToCart: spawn a <fly-to-cart>
          // custom element with the customer's hero-art image, source = the
          // modal ATC button, destination = the header cart icon. The
          // custom element is registered globally by product-form.js
          // (line ~566). We skip the animation if the cart icon is offscreen
          // (header hidden under modal backdrop on tiny viewports) so the
          // animation doesn't fly nowhere.
          try {
            var _cartIcon = document.querySelector('.header-actions__cart-icon');
            if (_cartIcon && typeof customElements !== 'undefined' && customElements.get('fly-to-cart')) {
              var _cartRect = _cartIcon.getBoundingClientRect();
              if (_cartRect.width > 0 && _cartRect.height > 0) {
                // Source image: customer's hero art (R.artDisplayUrl), with a
                // fallback to the currently-visible slide in the modal in case
                // R isn't fully hydrated.
                var _imgUrl = (R_atc && R_atc.artDisplayUrl) || '';
                if (!_imgUrl) {
                  var _activeSlide = (function () {
                    var modal_ = (typeof getModal === 'function') ? getModal() : null;
                    var _key = R_atc.activeFormat === 'paint_kit' ? 'paint-kit'
                            : R_atc.activeFormat === 'canvas_framed' ? 'canvas-framed'
                            : 'canvas';
                    var _track = modal_ && modal_.querySelector('[data-wc-element="slider-track-' + _key + '"]');
                    var _idx = (window.WC._slider && window.WC._slider[R_atc.activeFormat]) || 0;
                    var _img = _track && _track.querySelectorAll('img')[_idx];
                    return _img && _img.src;
                  })();
                  _imgUrl = _activeSlide || '';
                }
                if (_imgUrl) {
                  var _fly = document.createElement('fly-to-cart');
                  _fly.style.setProperty('background-image', 'url(' + _imgUrl + ')');
                  _fly.source = atcBtn;
                  _fly.destination = _cartIcon;
                  document.body.appendChild(_fly);
                }
              }
            }
          } catch (_) { /* animation is cosmetic — never block the cart-add */ }

          // Collect cart-items-component section IDs for morph-on-response.
          var cartSectionIds = [];
          document.querySelectorAll('cart-items-component').forEach(function (el) {
            var id = el && el.dataset && el.dataset.sectionId;
            if (id && cartSectionIds.indexOf(id) === -1) cartSectionIds.push(id);
          });
          var bodyObj = { items: [{ id: String(vId), quantity: 1 }] };
          if (cartSectionIds.length) bodyObj.sections = cartSectionIds.join(',');

          // Match Horizon product-form.js request shape (Cloudflare bot mgmt
          // serves 429 HTML challenge if shape differs).
          function _toFormData(o) {
            var fd = new FormData();
            function appendKv(k, v) {
              if (v == null) return;
              if (Array.isArray(v)) v.forEach(function (vv, i) { appendKv(k + '[' + i + ']', vv); });
              else if (typeof v === 'object') Object.keys(v).forEach(function (kk) { appendKv(k + '[' + kk + ']', v[kk]); });
              else fd.append(k, String(v));
            }
            Object.keys(o).forEach(function (k) { appendKv(k, o[k]); });
            return fd;
          }

          // Cart-add WITH RETRY — ports wolden-customize.js wcCartAddWithRetry.
          // Shopify's Cloudflare bot management sometimes bounces the FIRST
          // /cart/add.js with an HTML challenge (or a transient 429/5xx); a
          // short backoff retry carries the clearance and succeeds. Retries
          // ONLY on challenge/transient responses (HTML, 429, 502, 503, 504),
          // never on a successful JSON add — so it cannot double-add. This
          // closes the gap where a real buyer's first add showed "Try again".
          function _cartAddWithRetry(reqBody, attempt) {
            attempt = attempt || 1;
            var BACKOFF_MS = [0, 1200, 3600];
            var MAX_ATTEMPT = 3;
            return new Promise(function (resolve, reject) {
              fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
                body: _toFormData(reqBody),
                credentials: 'same-origin',
              }).then(function (resp) {
                var ctype = ((resp.headers && resp.headers.get('Content-Type')) || '').toLowerCase();
                var isHtml = ctype.indexOf('text/html') === 0;
                var status = resp.status;
                var retryable = isHtml || status === 429 || status === 502 || status === 503 || status === 504;
                if (retryable && attempt < MAX_ATTEMPT) {
                  var ra = resp.headers && resp.headers.get('Retry-After');
                  var explicit = null;
                  if (ra) { var n = parseInt(ra, 10); if (!isNaN(n) && n > 0 && n < 10) explicit = n * 1000; }
                  var delay = explicit != null ? explicit : BACKOFF_MS[attempt];
                  log('cart/add.js attempt ' + attempt + ' retryable (' + (isHtml ? 'html' : status) + ') — retry in ' + delay + 'ms');
                  setTimeout(function () { _cartAddWithRetry(reqBody, attempt + 1).then(resolve, reject); }, delay);
                  return;
                }
                if (isHtml) {
                  var e1 = new Error('Cart temporarily unavailable. Please try again in a moment.');
                  e1.wcUserFacing = true; reject(e1); return;
                }
                resp.json().then(function (parsed) {
                  if (!resp.ok) {
                    if (parsed && (parsed.description || parsed.message)) { reject(new Error(parsed.description || parsed.message)); return; }
                    var e2 = new Error('Cart temporarily unavailable. Please try again in a moment.'); e2.wcUserFacing = true; reject(e2); return;
                  }
                  resolve(parsed);
                }).catch(function () {
                  var e3 = new Error('Cart temporarily unavailable. Please try again in a moment.'); e3.wcUserFacing = true; reject(e3);
                });
              }).catch(function (netErr) {
                if (netErr && netErr.name === 'AbortError') { reject(netErr); return; }
                if (attempt < MAX_ATTEMPT) {
                  log('cart/add.js network error attempt ' + attempt + ' — retry: ' + (netErr && netErr.message));
                  setTimeout(function () { _cartAddWithRetry(reqBody, attempt + 1).then(resolve, reject); }, BACKOFF_MS[attempt]);
                  return;
                }
                var e4 = new Error('Cart temporarily unavailable. Please check your connection and try again.'); e4.wcUserFacing = true; reject(e4);
              });
            });
          }

          // 2026-07-02 — Call site A: cart-attribute stitching (tracking spec
          // §1.3 / §2.1 step 6). Stamp split-test identity (_aid / _wc_flow /
          // _wc_sub / _ph_distinct_id / …) onto the cart IN PARALLEL with the
          // add — fire-and-forget, never awaited (ATC latency is sacred;
          // /cart/update.js and /cart/add.js hit the same session cart, so
          // completion order doesn't matter). The resolved bool rides the
          // add_to_cart event below as cart_attrs_written (null = the write
          // was still in flight when the event fired).
          var _attrsWritten = null;
          try {
            wcWriteCartAttributes().then(function (ok) { _attrsWritten = !!ok; })['catch'](function () { _attrsWritten = false; });
          } catch (eAttrs) { /* stitching is best-effort — never blocks ATC */ }

          _cartAddWithRetry(bodyObj).then(function (parsed) {
            atcBtn.disabled = false;
            if (atcBtn.firstChild && prevText) atcBtn.firstChild.nodeValue = prevText;
            // Notify Horizon's cart-items-component + cart-drawer-component.
            try {
              var ev = new Event('cart:update', { bubbles: true });
              ev.detail = {
                resource: parsed,
                sourceId: 'wolden-customize-modal',
                data: {
                  source: 'wolden-customize-modal',
                  itemCount: (parsed.items && parsed.items.length) || 1,
                  variantId: vId,
                  sections: parsed.sections || {},
                },
              };
              document.dispatchEvent(ev);
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
              document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
            } catch (e9) { log('cart:update dispatch failed', e9 && e9.message); }
            var pr = (typeof getCurrentPricing === 'function') ? getCurrentPricing() : {};
            track('add_to_cart', {
              generation_id: R_atc.generationId,
              bubble_id: R_atc.bubbleId,
              variant_id: vId,
              format: R_atc.activeFormat,
              size: R_atc.activeFormat === 'canvas' ? R_atc.activeCanvasSize
                  : R_atc.activeFormat === 'canvas_framed' ? R_atc.activeCanvasFramedSize
                  : R_atc.activePaintKitSize,
              frame: R_atc.activeFormat === 'paint_kit' ? R_atc.activePaintKitFrame : null,
              unit_price: (pr && pr.sale) || null,
              // 2026-07-02 — spec §2.1 step 6 additions: value + atc_path +
              // whether the §1.3 cart-attribute stamp had landed by emit time.
              value: (pr && pr.sale) || null,
              atc_path: 'modal',
              cart_attrs_written: _attrsWritten,
              currency: 'USD',
              reveal_experience: getActiveVariant(),
            });
          }).catch(function (err) {
            atcBtn.disabled = false;
            if (atcBtn.firstChild && prevText) atcBtn.firstChild.nodeValue = prevText;
            log('cart/add failed after retries', err && err.message);
            if (atcBtn.firstChild) atcBtn.firstChild.nodeValue = ' Try again ';
            setTimeout(function () { if (atcBtn.firstChild && prevText) atcBtn.firstChild.nodeValue = prevText; }, 2500);
          });
          return;
        }
        if (action === 'select-paintkit-size') {
          e.preventDefault();
          // 2026-05-14 v7 — Capture pre-mutation slot. Per spec: PBN size
          // does NOT change gallery images, but Horizon variant:changed
          // may reset slot anyway — re-pin via renderUnifiedGallery below.
          if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
          var ps = target.getAttribute('data-wc-size');
          if (ps && ps !== window.WC._currentResult.activePaintKitSize) {
            if (typeof syncRadioGroupBy === 'function') {
              syncRadioGroupBy('select-paintkit-size', 'data-wc-size', ps);
            }
            window.WC._currentResult.activePaintKitSize = ps;
            refreshSizeFramePills();
            if (typeof refreshMockupImages === 'function') refreshMockupImages();
            refreshPricingDisplay();
            updatePdpVariantState(); // Phase A v1.1 — sync PDP-side variant ID + price
            if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
            track('size_selected', {
              generation_id: window.WC._currentResult.generationId,
              format: 'paint_kit',
              size: ps,
            });
          }
          return;
        }
        if (action === 'select-paintkit-frame') {
          e.preventDefault();
          if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
          var fr = target.getAttribute('data-wc-frame');
          if (fr && fr !== window.WC._currentResult.activePaintKitFrame) {
            window.WC._currentResult.activePaintKitFrame = fr;
            if (typeof syncRadioGroupBy === 'function') {
              syncRadioGroupBy('select-paintkit-frame', 'data-wc-frame', fr);
            }
            refreshSizeFramePills();
            refreshPricingDisplay();
            updatePdpVariantState(); // Phase A v1.1 — sync PDP-side variant ID + price
            if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
            track('frame_selected', {
              generation_id: window.WC._currentResult.generationId,
              frame: fr,
              size: window.WC._currentResult.activePaintKitSize,
            });
          }
          return;
        }
        if (action === 'select-canvas-framed-size') {
          e.preventDefault();
          // 2026-05-14 v6 — Capture PDP slideshow slot BEFORE state mutation
          // so the deferred clean-break (firing on modal close) can preserve
          // the customer's current PDP gallery slot. Modal-side slide deck
          // has its own state and isn't affected here.
          if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
          var mcfs = target.getAttribute('data-wc-size');
          if (mcfs && mcfs !== window.WC._currentResult.activeCanvasFramedSize) {
            window.WC._currentResult.activeCanvasFramedSize = mcfs;
            if (typeof syncRadioGroupBy === 'function') {
              syncRadioGroupBy('select-canvas-framed-size', 'data-wc-size', mcfs);
            }
            refreshActiveBranches();
            refreshSizeFramePills();
            if (typeof refreshMockupImages === 'function') refreshMockupImages();
            refreshPricingDisplay();
            updatePdpVariantState();
            // Modal-driven size change — defer URL/title/gallery swap until modal close.
            applyCleanBreakDeferred('modal-canvas-framed-size-change');
            // Re-paint PDP gallery so slot model + slot preservation apply now.
            if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
            track('size_selected', {
              generation_id: window.WC._currentResult.generationId,
              format: 'canvas_framed',
              size: mcfs,
            });
          }
          return;
        }
        if (action === 'select-canvas-framed-color') {
          e.preventDefault();
          // 2026-05-14 v6 — Capture PDP slideshow slot BEFORE state mutation
          // (see select-canvas-framed-size modal handler for rationale).
          if (typeof captureFramedSlotIntent === 'function') captureFramedSlotIntent();
          var mcfc = target.getAttribute('data-wc-color');
          if (mcfc && mcfc !== window.WC._currentResult.activeCanvasFramedColor) {
            window.WC._currentResult.activeCanvasFramedColor = mcfc;
            if (typeof syncRadioGroupBy === 'function') {
              syncRadioGroupBy('select-canvas-framed-color', 'data-wc-color', mcfc);
            }
            refreshSizeFramePills();
            // 2026-05-14 v8.21 — Missing call vs. sibling modal handlers
            // (size, format) and the PDP color handler. Without this, the
            // state mutates but the MODAL SLIDER <img> srcs don't repaint,
            // so color changes only became visible on the next size click.
            if (typeof refreshMockupImages === 'function') refreshMockupImages();
            updatePdpVariantState();
            // Color doesn't change the URL/handle — same product, different variant.
            if (typeof renderUnifiedGallery === 'function') renderUnifiedGallery();
            track('frame_color_selected', {
              generation_id: window.WC._currentResult.generationId,
              format: 'canvas_framed',
              color: mcfc,
            });
          }
          return;
        }

        // ── Reopen-pill / minimize actions ──────────────────────────────────
        if (action === 'reopen-result' || action === 'reopen-from-state-b') {
          e.preventDefault();
          try { reopenLastResult(); } catch (err) { log('reopenLastResult error', err && err.message); }
          return;
        }
        if (action === 'dismiss-reopen-pill') {
          e.preventDefault();
          var rp = document.querySelector('[data-wc-component="reopen-pill"]');
          if (rp) rp.hidden = true;
          return;
        }
        if (action === 'expand-progress-pill') {
          e.preventDefault();
          // Minimal: re-show the modal in generating state.
          try { setModalState('generating'); } catch (err) { /* non-fatal */ }
          window.WC._minimized = false;
          var pp = document.querySelector('[data-wc-component="progress-pill"]');
          if (pp) pp.hidden = true;
          return;
        }
        if (action === 'minimize-modal') {
          e.preventDefault();
          try { minimizeModalToPill(); } catch (err) { log('minimizeModalToPill error', err && err.message); }
          return;
        }

        target = target.parentNode;
      }
    });
  }

// ─── Public surface ────────────────────────────────────────────────────────

  // 2026-05-19 — V1→V2 port: pill-level click delegate.
  // The bottom-right "View Your Art" reopen pill lives OUTSIDE the modal
  // element (direct child of <body>). The modal click delegate at line
  // ~1901 never sees pill clicks — they fall through to document with no
  // handler bound. V1 had initReopenPill() to bind a separate listener
  // on the pill itself; V2 lost that and the pill silently no-op'd.
  // Port of monolith lines 7622-7647.
  // Reopen the LIVE loading screen — used while a gen is still in-flight by the
  // reopen-pill, the progress-pill, and the dropzone State-B button. No result
  // exists yet, so reopenLastResult/reopenFromStateB would have nothing to show.
  function _reopenLiveLoader() {
    try { setModalState('generating'); } catch (err) { /* non-fatal */ }
    if (window.WC) window.WC._minimized = false;
    var pp = document.querySelector('[data-wc-component="progress-pill"]'); if (pp) pp.hidden = true;
    var rp = document.querySelector('[data-wc-component="reopen-pill"]');   if (rp) rp.hidden = true;
  }

  function initReopenPill() {
    var pill = document.querySelector('[data-wc-component="reopen-pill"]');
    if (!pill || pill.__wcInitialized) return;
    pill.__wcInitialized = true;
    pill.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest('[data-wc-action]');
      if (!trigger) return;
      var action = trigger.getAttribute('data-wc-action');
      if (action === 'reopen-result') {
        e.preventDefault();
        // If a gen is still running (e.g. a prior result's pill is showing when
        // a new gen starts), reopen the live loader rather than the stale result.
        if (window.WC && window.WC._genInFlight) { _reopenLiveLoader(); return; }
        // Prefer in-memory stash (current session). On fresh page load with
        // saved gens in LocalStorage, reopenLastResult itself falls back to
        // reopenFromStateB (see wc-pdp-swap-15.js).
        try { reopenLastResult(); } catch (err) { log('reopenLastResult error', err && err.message); }
      } else if (action === 'dismiss-reopen-pill') {
        e.preventDefault();
        // V1 nulled WC._lastResult here. We keep _lastResult intact so
        // the customer can re-summon via /my-generations or a fresh
        // upload — but we hide the pill until the next gen-complete.
        pill.hidden = true;
      }
    });
  }

  // The progress-pill (shown while a gen runs and the modal is minimized) lives
  // OUTSIDE the modal element, just like the reopen-pill — so the modal click
  // delegate never sees its clicks. Bind a dedicated listener (mirrors
  // initReopenPill) so "expand-progress-pill" reopens the live loading screen.
  function initProgressPill() {
    var pill = document.querySelector('[data-wc-component="progress-pill"]');
    if (!pill || pill.__wcInitialized) return;
    pill.__wcInitialized = true;
    pill.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest('[data-wc-action]');
      if (!trigger) return;
      if (trigger.getAttribute('data-wc-action') === 'expand-progress-pill') {
        e.preventDefault();
        _reopenLiveLoader();
      }
    });
  }

  // initSelectors: wire up all PDP + modal selector handlers.
  // Call from wc-init.js initAll(), after the modal shell exists.
  export function initSelectors(rootEl, gen) {
    // rootEl and gen are accepted for forward-compat with the wc-init.js call
    // convention; the current implementation uses document-scoped listeners
    // and WC._currentResult global state (matching monolith behavior).
    initPdpOptionsListener();
    initPdpOptionsState();
    initModalSelectorHandlers();
    initReopenPill();
    initProgressPill();
    initModalSwipeHandlers();
    installFramedGallerySrcGuard();
  }

  // getCurrentSelection: read current selector state from WC._currentResult.
  // Returns { format, size, frame, color, variantId }.
  export function getCurrentSelection(rootEl) {
    var R = window.WC._currentResult;
    if (!R) return null;
    return {
      format:    R.activeFormat,
      size:      R.activeFormat === 'canvas'         ? R.activeCanvasSize
               : R.activeFormat === 'canvas_framed'  ? R.activeCanvasFramedSize
               : R.activePaintKitSize,
      frame:     R.activeFormat === 'paint_kit'      ? R.activePaintKitFrame
               : R.activeFormat === 'canvas_framed'  ? 'Framed'
               : null,
      color:     R.activeFormat === 'canvas_framed'  ? R.activeCanvasFramedColor : null,
      variantId: getCurrentVariantId(),
    };
  }

  // ─── applyPdpVisibility ─────────────────────────────────────────────────────
  // Ported from monolith lines 4875-4920. Phase A — PDP-only visibility:
  // format selector + pdp-options wrap hidden until a generation exists, OR
  // the customer is on a child PDP URL (custom-canvas-print-XXXX, etc.).
  // V2 callers (wc14-result.js, wc14-upload.js) reference this as a bare
  // identifier behind `typeof === 'function'` checks; without it being
  // defined here, every call was a silent no-op and post-gen child PDPs
  // never revealed their selectors.
  function applyPdpVisibility() {
    var R = window.WC._currentResult;
    var formatRow = document.querySelector('[data-wc-element="pdp-format-row"]');
    var onChildPdpUrl = /^\/products\/(custom-canvas-print-|custom-canvas-framed-|custom-paint-by-numbers-kit-)/i.test(window.location.pathname);
    var hasGen = !!(R && R.generationId);
    var showSelectors = hasGen || onChildPdpUrl;
    if (formatRow) formatRow.hidden = !showSelectors;
    var pdpOptionsWrap = document.querySelector('[data-wc-component="pdp-options"]');
    if (pdpOptionsWrap) {
      pdpOptionsWrap.classList.toggle('wc-show-selectors', showSelectors);
      pdpOptionsWrap.classList.toggle('wc-pre-gen', !showSelectors);
    }
    if (document.body) {
      document.body.classList.toggle('wc-has-gen', showSelectors);
    }
    // ATC label gate (post-gen-only "Customize & Add to Cart" → "Add to Cart").
    // Bare typeof check — port of monolith's syncAtcAnimationToGenerationState
    // is deferred (ATC animation gating is polish, not part of the user-
    // reported issue). If/when ported, drop it in here.
    if (typeof refreshAtcLabelForPreGen === 'function') refreshAtcLabelForPreGen();
  }

  // Also surface on window.WC so monolith-style bare `applyPdpVisibility()`
  // calls inside V2 modules (which run with no shared lexical scope) can
  // find it. V1 set it on the WC namespace via the wolden-customize.js
  // closure; in V2 each module is independent so we publish here.
  try {
    window.WC = window.WC || {};
    window.WC.applyPdpVisibility = applyPdpVisibility;
  } catch (_) {}

// Re-export helpers called by other modules (wc-result.js, wc-init.js, etc.)
export {
  refreshActiveBranches,
  refreshSizeFramePills,
  refreshPricingDisplay,
  getCurrentPricing,
  getCurrentVariantId,
  updatePdpVariantState,
  renderUnifiedGallery,
  renderCanvasFramedPdpGallery,
  buildSlotModelForFormat,
  applySlotModelToGallery,
  getPrimaryGalleryScope,
  resetFormatDefaults,
  applyPdpVisibility,
  captureFramedSlotIntent,
  syncRadioGroupBy,
  transitionGalleryForFormatChange,
  restorePdpGalleryVisibility,
  installFramedGallerySrcGuard,
  teardownFramedGallerySrcGuard,
  preloadFramedMockups,
  lookupFramedMockup,
  applyFramedMockupToGallery,
  setSlideIndex,
  nextSlide,
  prevSlide,
  /* 2026-07-01 (Flow B) — exported so wc77-flow-b.js writes its _pet_name
     line-item property through the SAME native-form writer the other eight
     properties[_*] inputs use (same scope resolution, same replace-not-
     duplicate semantics), instead of duplicating the form-scope logic. */
  _writeFormProperty as writeFormProperty,
  fmtPrice,
  fmtSavings,
  CANVAS_PRICING,
  CANVAS_FRAMED_PRICING,
  CANVAS_FRAMED_COLORS,
  CANVAS_FRAMED_COLOR_HEX,
  PAINT_KIT_PRICING,
  PAINT_KIT_CANVAS_LABEL,
  FRAME_TO_VARIANT_KEY,
  SLIDE_COUNTS,
  CANVAS_SIZE_TO_SLIDE,
};

/* Expose refresh helpers on window.WC so cross-module callers (e.g. the
   experiment renderers in wc50-experiments.js, like Version D) can drive the
   existing refresh chain without ESM circular imports. Mirrors the existing
   wc.initUploadZoneState attachment in wc50-upload.js. */
try {
  if (typeof window !== 'undefined') {
    var __W_WC = (window.WC = window.WC || {});
    __W_WC.refreshActiveBranches      = refreshActiveBranches;
    __W_WC.refreshSizeFramePills      = refreshSizeFramePills;
    __W_WC.refreshMockupImages        = refreshMockupImages; /* 2026-05-25 — needed by Version D _wcDRefreshChain so framed slide-front/-context img.src actually re-key on color/size change. Without this attach, the chain call silently no-ops because window.WC.refreshMockupImages is undefined, leaving slide-canvas-framed-front stuck on its initial src. */
    __W_WC.refreshPricingDisplay      = refreshPricingDisplay;
    __W_WC.updatePdpVariantState      = updatePdpVariantState;
    __W_WC.renderUnifiedGallery       = renderUnifiedGallery;
    __W_WC.applyFramedMockupToGallery = applyFramedMockupToGallery;
    __W_WC.setSlideIndex              = setSlideIndex;
    __W_WC._setSelected               = _setSelected; /* 2026-05-25 — Version D drives its -d-suffixed pills/swatches through the canonical _setSelected helper (sets radio.checked + toggles label.is-checked + paints inline styles). Exposing it here means the Version D rebuild reuses the IDENTICAL active-state styling that Paint Kit Frame / Canvas Framed Color already use, instead of reinventing CSS classes. */
    /* 2026-07-02 QA round-3 — expose the canonical price tables so Version
       D's "Add a frame" upsell card (wc77-experiments.js
       _wcDComputeUpsellDelta) computes its struck/sale numbers from the SAME
       source of truth the price line uses, instead of a hardcoded copy.
       Same no-circular-import channel as the helpers above. */
    __W_WC.CANVAS_PRICING             = CANVAS_PRICING;
    __W_WC.CANVAS_FRAMED_PRICING      = CANVAS_FRAMED_PRICING;
    /* 2026-05-27 — Expose canonical click-handler side-effect helpers needed by
       Version D's _wcDOnClick to restore behavior parity with the original
       select-canvas-framed-size / select-canvas-framed-color / select-format
       handlers. Without these, Version D's relocated selectors trigger refresh
       but skip side effects that the original handlers perform:
         - captureFramedSlotIntent: pin Horizon <slideshow-component> to the
           current slot BEFORE updatePdpVariantState fires variant:changed.
           Without this, the slideshow resets to slot 0 (art) mid-chain and the
           customer sees the original art instead of the newly-keyed framed
           mockup → the "gallery doesn't update on size click in Frame mode"
           bug.
         - transitionGalleryForFormatChange: ~400ms cross-fade on format
           toggle; without it, Version D produces a jarring instant cut.
         - resetFormatDefaults: canonical per-format state reset; Version D's
           inline manual seeding misses cross-format fields. */
    __W_WC.captureFramedSlotIntent       = captureFramedSlotIntent;
    __W_WC.transitionGalleryForFormatChange = transitionGalleryForFormatChange;
    __W_WC.resetFormatDefaults           = resetFormatDefaults;
  }
} catch (_) {}
